import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Spinner, Row, Col, Tabs, Tab, Badge, ProgressBar, Modal, Table, Accordion } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileUpload, faCog, faHistory, faSync, faDownload, faCheck, faExclamationTriangle, faRobot, faPen, faPlus, faTrash, faInfoCircle, faTimes, faCheckCircle, faPhone, faExclamationCircle, faCopy, faClock, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { useUserContext } from '../layouts/UserContext';
import { getHatateToken } from '../helpers/utils';
import axios from 'axios';

// ================ TIPOS E INTERFACES ================

type FileUploadProcessorProps = {
    language: 'es' | 'en';
    disabled?: boolean;
};

// ✨ NUEVAS INTERFACES PARA WHATSAPP RISK METER
interface WhatsAppRiskFactor {
    id: string;
    name: string;
    description: string;
    weight: number;
    currentValue: any;
    optimalRange: any;
    score: number; // 0-100
    status: 'excellent' | 'good' | 'warning' | 'danger';
    recommendation?: string;
}

interface WhatsAppRiskAssessment {
    overallScore: number; // 0-100
    riskLevel: 'low' | 'medium' | 'high' | 'critical';
    factors: WhatsAppRiskFactor[];
    recommendations: string[];
    flagProbability: number; // 0-100
}

interface FileHistoryItem {
    id: string;
    originalName: string;
    fileName: string;
    processedDate: string;
    status: 'success' | 'error' | 'processing';
    contactCount: number;
    validContactCount: number;
    totalRowsProcessed: number;
    outputFormat: string[];
    fileSize: number;
    processingTimeMs: number;
    userId?: string;
    errorMessage?: string;
    serverPath?: string;
    correctedPhones?: number;
    duplicatesRemoved?: number;
}

interface ProcessorConfig {
    enabled: boolean;
    autoDownload: boolean;
    lastUpdated?: number;
    // ✨ NUEVAS CONFIGURACIONES DE HORARIOS
    timeIntervalInit?: string;
    timeIntervalEnd?: string;
    daysInterval?: string;
}

interface Variable {
    id: string;
    name: string;
    value: string;
}

interface ContactData {
    nombre: string;
    telefono: string;
    curso: string;
    mensaje: string;
}

interface PhoneValidationIssue {
    rowIndex: number;
    originalPhone: string;
    correctedPhone: string;
    issueType: 'incomplete' | 'invalid_format' | 'missing_country_code';
    contactName: string;
}

interface DuplicateContact {
    rowIndex: number;
    phone: string;
    name: string;
    originalRowIndex: number;
}

interface ValidationResult {
    isValid: boolean;
    errors: string[];
    warnings: string[];
    totalRows: number;
    validContacts: number;
    phoneIssues: PhoneValidationIssue[];
    duplicates: DuplicateContact[];
}

interface ProcessingStats {
    startTime: number;
    endTime?: number;
    totalRows: number;
    validContacts: number;
    skippedRows: number;
    errorRows: number;
    correctedPhones: number;
    duplicatesRemoved: number;
}

// ✨ NUEVA INTERFAZ PARA EL ESTADO DE WHATSAPP
interface WhatsAppStatus {
    envExists: boolean;
    envValid: boolean;
    scheduleConfigured: boolean;
    ready: boolean;
    recommendations: any[];
    schedule?: {
        timeIntervalInit?: string;
        timeIntervalEnd?: string;
        daysInterval?: string[];
        summary?: string;
    };
}

interface FirstResponseContext {
    key: string;
    name: string;
    description: string;
}

interface FirstResponseConfig {
    enabled: boolean;
    context: string;
}

interface FirstResponseStats {
    responsesToday?: number;
    totalTrackedUsers?: number;
    availableContexts?: number;
}

// ================ COMPONENTE PRINCIPAL ================

const FileUploadProcessor: React.FC<FileUploadProcessorProps> = ({ language, disabled = false }) => {
    const { user } = useUserContext();
    
    // Obtener nombre de la company del usuario (fallback a "Company" si no existe)
    const companyName = user?.company || 'Company';
    const companyNameLower = companyName.toLowerCase();
    const API_KEY='anubis-whatsapp-api-key';
    
    const companyMail = user?.email || 'Email';
    const companyMailLower = companyMail.toLowerCase();
    const companyDomain = companyMailLower.includes('@') 
        ? companyMailLower.split('@')[1] 
        : companyMailLower;

    // Estados para la configuración
    const [config, setConfig] = useState<ProcessorConfig>({
        enabled: true,
        autoDownload: false,
        // ✨ VALORES POR DEFECTO PARA HORARIOS
        timeIntervalInit: '09:00',
        timeIntervalEnd: '18:00',
        daysInterval: 'MON,TUE,WED,THU,FRI'
    });
    
    // ✨ NUEVO ESTADO PARA WHATSAPP
    const [whatsAppStatus, setWhatsAppStatus] = useState<WhatsAppStatus | null>(null);
    const [isLoadingWhatsApp, setIsLoadingWhatsApp] = useState(false);
    const [isSavingSchedule, setIsSavingSchedule] = useState(false);
    
    // Estados para el historial de archivos
    const [fileHistory, setFileHistory] = useState<FileHistoryItem[]>([]);
    const [totalFiles, setTotalFiles] = useState(0);
    
    // Estados para subir/procesar archivos
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [processingProgress, setProcessingProgress] = useState(0);
    const [previewData, setPreviewData] = useState<any[]>([]);
    const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
    
    // Estados generales
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [processedData, setProcessedData] = useState<ContactData[] | null>(null);
    const [contactCount, setContactCount] = useState(0);
    const [processingStats, setProcessingStats] = useState<ProcessingStats | null>(null);

    // Estados para manejar tabs
    const [activeTab, setActiveTab] = useState('upload');
    const [historyRefreshInterval, setHistoryRefreshInterval] = useState<NodeJS.Timeout | null>(null);
    
    // Estados para variables y mensaje personalizado
    const [variables, setVariables] = useState<Variable[]>([]);
    const [customMessage, setCustomMessage] = useState(`Hola [nombre], te saludamos desde ${companyName} ya que has consultado el curso [curso].\n\nLos detalles del curso son:\n📅 Clases desde [fecha_inicio] hasta [fecha_fin]\n👨‍🏫 Docentes: [docentes]\n🔗 Link de información: [enlace_curso]\n💰 Precio: [precio]\n\n¡Esperamos tu participación! 🎓`);
    const [isCancelling, setIsCancelling] = useState(false);
    const [toast, setToast] = useState({
    show: false,
    variant: '',
    title: '',
    message: '',
    autoHide: true,
    duration: 4000
});
const showToast = (variant, title, message, autoHide = true, duration = 4000) => {
    if (!title || !message) {
        console.warn('Toast llamado sin título o mensaje:', { variant, title, message });
        return; // No mostrar toast si faltan datos
    }
    
    setToast({
        show: true,
        variant: `subtle-${variant}`,
        title,
        message,
        autoHide,
        duration
    });
    
    if (autoHide) {
        setTimeout(() => {
            hideToast();
        }, duration);
    }
};

const hideToast = () => {
    setToast(prev => ({ ...prev, show: false }));
};

// Funciones específicas para diferentes tipos de toast
const showSuccessToast = (title, message, duration = 4000) => {
    showToast('success', title, message, true, duration);
};

const showErrorToast = (title, message, duration = 6000) => {
    showToast('danger', title, message, true, duration);
};

const showWarningToast = (title, message, duration = 5000) => {
    showToast('warning', title, message, true, duration);
};

const showInfoToast = (title, message, duration = 4000) => {
    showToast('info', title, message, true, duration);
};

    // Estados para modal de éxito
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successModalData, setSuccessModalData] = useState<{
        fileName: string;
        contactCount: number;
        processingTime: string;
        filePath?: string;
        correctedPhones?: number;
        duplicatesRemoved?: number;
    } | null>(null);
    
    // Estados para modal de validación de teléfonos
    const [showPhoneValidationModal, setShowPhoneValidationModal] = useState(false);
    const [phoneValidationData, setPhoneValidationData] = useState<{
        issues: PhoneValidationIssue[];
        duplicates: DuplicateContact[];
        onConfirm: () => void;
        onCancel: () => void;
    } | null>(null);
    
    // ================ EASTER EGG TOASTY ================
    const [showToasty, setShowToasty] = useState(false);

    // Estados para primera respuesta
    const [firstResponseConfig, setFirstResponseConfig] = useState<FirstResponseConfig>({
        enabled: false,
        context: 'marketing_general'
    });
    const [isLoadingFirstResponse, setIsLoadingFirstResponse] = useState<boolean>(false);
    const [isSavingFirstResponse, setIsSavingFirstResponse] = useState<boolean>(false);
    const [firstResponseStats, setFirstResponseStats] = useState<FirstResponseStats | null>(null);
    const [firstResponseContexts, setFirstResponseContexts] = useState<FirstResponseContext[]>([]);

    // ✨ ESTADOS PARA WHATSAPP RISK METER
    const [showRiskMeter, setShowRiskMeter] = useState(false);
    const [riskAssessment, setRiskAssessment] = useState<WhatsAppRiskAssessment | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [dragPosition, setDragPosition] = useState({ x: window.innerWidth - 150, y: window.innerHeight - 150 });
    const [isCalculatingRisk, setIsCalculatingRisk] = useState(false);
    const [lastUpdateTrigger, setLastUpdateTrigger] = useState(0);

useEffect(() => {
    // Solo recalcular si hay datos suficientes
    if (customMessage || variables.length > 0) {
        // Sin debounce para actualización inmediata mientras se escribe
        const assessment = calculateRiskAssessment();
        setRiskAssessment(assessment);
    }
}, [
    // Triggers que deben recalcular el riesgo INMEDIATAMENTE
    customMessage, // Se actualiza mientras escribes
    variables, // Se actualiza cuando agregas/quitas variables
    contactCount,
    config.timeIntervalInit,
    config.timeIntervalEnd,
    config.daysInterval,
    firstResponseConfig.enabled,
    firstResponseConfig.context,
    whatsAppStatus?.ready,
    whatsAppStatus?.envExists,
    whatsAppStatus?.envValid,
    whatsAppStatus?.scheduleConfigured,
    lastUpdateTrigger
]);

// ✨ FUNCIÓN PARA FORZAR ACTUALIZACIÓN
const triggerRiskUpdate = () => {
    setLastUpdateTrigger(Date.now());
};

    // ================ ✨ FUNCIONES PARA WHATSAPP RISK METER ================
const calculateFinalMessageLength = (): { 
    finalMessage: string; 
    finalLength: number; 
    rawLength: number;
    linkCount: number;
    emojiCountFinal: number;
} => {
    if (!customMessage) return { 
        finalMessage: '', 
        finalLength: 0, 
        rawLength: 0, 
        linkCount: 0,
        emojiCountFinal: 0 
    };
    
    const rawLength = customMessage.length;
    
    // Simular reemplazo de variables con valores realistas
    let processedMessage = customMessage;
    
    // Reemplazar variables básicas
    processedMessage = processedMessage.replace(/\[nombre\]/g, 'Juan Pérez');
    processedMessage = processedMessage.replace(/\[curso\]/g, 'Curso de Programación Web Avanzada');
    
    // Reemplazar variables personalizadas
    variables.forEach(variable => {
        if (variable.name && variable.value) {
            let finalValue = variable.value;
            
            // Si es variable monetaria, formatear
            if (isMonetaryVariable(variable.name)) {
                finalValue = formatMonetaryValue(variable.value);
            }
            // Si la variable contiene 'enlace', 'link', 'url', simular URL real
            else if (variable.name.toLowerCase().includes('enlace') || 
                     variable.name.toLowerCase().includes('link') || 
                     variable.name.toLowerCase().includes('url')) {
                finalValue = `https://ejemplo.com/curso-info?id=12345&ref=whatsapp`;
            }
            
            const regex = new RegExp(`\\[${variable.name}\\]`, 'g');
            processedMessage = processedMessage.replace(regex, finalValue);
        }
    });
    
    // Contar enlaces usando la función definida arriba
    const linkCount = countLinksInMessage(processedMessage);
    
    // Contar emojis en el mensaje final
    const emojiCountFinal = (processedMessage.match(/[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu) || []).length;
    
    return {
        finalMessage: processedMessage,
        finalLength: processedMessage.length,
        rawLength: rawLength,
        linkCount: linkCount,
        emojiCountFinal: emojiCountFinal
    };
};
// ================ SISTEMA DE RIESGO ESTRICTO Y REALISTA ================
// ✨ FUNCIÓN PARA CONTAR ENLACES EN MENSAJE
const countLinksInMessage = (message: string): number => {
    if (!message) return 0;
    
    // Patrón único más robusto que captura todos los tipos de enlaces
    const urlPattern = /(?:https?:\/\/|www\.|\b(?:bit\.ly|tinyurl\.com|t\.co)\/)[^\s]+/gi;
    const matches = message.match(urlPattern);
    
    // Usar Set para eliminar duplicados en caso de que los haya
    const uniqueLinks = new Set(matches ? matches.map(link => link.toLowerCase()) : []);
    
    return uniqueLinks.size;
};

// REEMPLAZAR TODA LA FUNCIÓN calculateRiskAssessment() por esta versión estricta:

const calculateRiskAssessment = (): WhatsAppRiskAssessment => {
    const factors: WhatsAppRiskFactor[] = [];
    
    // Factor 1: Mensajes por hora - MÁS ESTRICTO
    const messagesPerHour = contactCount;
    let messagesScore = 100;
    if (messagesPerHour <= 50) {
        messagesScore = 100; // Óptimo
    } else if (messagesPerHour <= 75) { // 50% más que 50
        messagesScore = 15; // ALTO RIESGO
    } else {
        messagesScore = 5; // CRÍTICO
    }
    
    factors.push({
        id: 'messages_per_hour',
        name: language === 'es' ? 'Mensajes por hora' : 'Messages per hour',
        description: language === 'es' ? 'Máximo 50 por hora, más de 75 es crítico' : 'Maximum 50 per hour, more than 75 is critical',
        weight: 15,
        currentValue: messagesPerHour,
        optimalRange: '≤ 50',
        score: messagesScore,
        status: messagesScore > 80 ? 'excellent' : messagesScore > 40 ? 'warning' : 'danger',
        recommendation: messagesScore < 40 ? (language === 'es' ? 'PELIGRO: Reducir urgentemente la velocidad de envío' : 'DANGER: Urgently reduce sending speed') : undefined
    });

    // Factor 2: Lista de contactos - MÁS ESTRICTO
    const contactListScore = contactCount <= 200 ? 100 : 
                            contactCount <= 300 ? 15 : // 50% más que 200
                            5; // CRÍTICO
    factors.push({
        id: 'contact_list',
        name: language === 'es' ? 'Tamaño de lista' : 'List size',
        description: language === 'es' ? 'Máximo 200 contactos, más de 300 es crítico' : 'Maximum 200 contacts, more than 300 is critical',
        weight: 12,
        currentValue: contactCount,
        optimalRange: '≤ 200',
        score: contactListScore,
        status: contactListScore > 80 ? 'excellent' : contactListScore > 40 ? 'warning' : 'danger',
        recommendation: contactListScore < 40 ? (language === 'es' ? 'PELIGRO: Lista muy grande, dividir en grupos menores' : 'DANGER: List too large, split into smaller groups') : undefined
    });

    // Factor 3: Longitud del mensaje - MÁS ESTRICTO
    //const { finalMessage, finalLength, rawLength, hasRealLinks, emojiCountFinal } = calculateFinalMessageLength();
    const { finalMessage, finalLength, rawLength, linkCount: detectedLinks, emojiCountFinal } = calculateFinalMessageLength();
let messageLengthScore = 100;

if (finalLength < 20) {
    // Mensajes muy cortos son sospechosos
    messageLengthScore = 30;
} else if (finalLength <= 250) {
    // ZONA ÓPTIMA: ≤250 caracteres = 100 puntos
    messageLengthScore = 100;
} else if (finalLength <= 350) {
    // ZONA MEDIA: 251-350 caracteres = de 100 a 50 puntos
    // Fórmula: 100 - ((caracteres - 250) / 100) * 50
    const excess = finalLength - 250;
    messageLengthScore = 100 - (excess / 100) * 50;
} else if (finalLength <= 550) {
    // ZONA RIESGOSA: 351-550 caracteres = de 50 a 5 puntos
    // Fórmula: 50 - ((caracteres - 350) / 200) * 45
    const excess = finalLength - 350;
    messageLengthScore = 50 - (excess / 200) * 45;
} else {
    // MÁS DE 550 caracteres = 0 puntos (crítico absoluto)
    messageLengthScore = 0;
}

// Redondear el score
messageLengthScore = Math.round(messageLengthScore);

factors.push({
    id: 'message_length',
    name: language === 'es' ? 'Longitud del mensaje' : 'Message length',
    description: language === 'es' ? '≤250 óptimo, 350 medio (50pts), 550+ crítico' : '≤250 optimal, 350 mid (50pts), 550+ critical',
    weight: 18,
    currentValue: `${finalLength} caracteres`,
    optimalRange: '≤ 250',
    score: messageLengthScore,
    status: messageLengthScore >= 80 ? 'excellent' : messageLengthScore >= 40 ? 'warning' : 'danger',
    recommendation: messageLengthScore < 40 ? (
        finalLength < 20 
            ? (language === 'es' ? 'PELIGRO: Mensaje demasiado corto, agregar contenido' : 'DANGER: Message too short, add content')
            : finalLength > 550
            ? (language === 'es' ? `CRÍTICO: Mensaje muy largo (${finalLength} chars), reducir a menos de 250` : `CRITICAL: Message too long (${finalLength} chars), reduce to under 250`)
            : (language === 'es' ? `ALTO RIESGO: Mensaje largo (${finalLength} chars), reducir para mejorar score` : `HIGH RISK: Long message (${finalLength} chars), reduce to improve score`)
    ) : messageLengthScore < 80 ? (
        language === 'es' ? `Mensaje en zona media (${finalLength} chars), considerar reducir a menos de 250 para score óptimo` : `Message in mid zone (${finalLength} chars), consider reducing to under 250 for optimal score`
    ) : undefined
});

// Factor 4: Emojis - CORREGIDO
let emojiScore = 100;
if (emojiCountFinal <= 4) {
    emojiScore = 100; // Óptimo
} else if (emojiCountFinal <= 6) { // 50% más que 2
    emojiScore = 65; // ALTO RIESGO
} else {
    emojiScore = 5; // CRÍTICO
}

factors.push({
    id: 'emoji_count',
    name: language === 'es' ? 'Iconos/Emojis' : 'Icons/Emojis',
    description: language === 'es' ? 'Máximo 4 emojis, más de 6 es crítico' : 'Maximum 4 emojis, more than 6 is critical',
    weight: 15,
    currentValue: emojiCountFinal,
    optimalRange: '≤ 4',
    score: emojiScore,
    status: emojiScore > 80 ? 'excellent' : emojiScore > 40 ? 'warning' : 'danger',
    recommendation: emojiScore < 40 ? (language === 'es' ? `PELIGRO: Reducir de ${emojiCountFinal} a máximo 4 emojis` : `DANGER: Reduce from ${emojiCountFinal} to maximum 2 emojis`) : undefined
});

// Factor 5: Enlaces - PENALIZACIÓN GRADUAL (25% por enlace)
//const { finalMessage, finalLength, rawLength, linkCount: detectedLinks, emojiCountFinal } = calculateFinalMessageLength();

let linkScore = 100;
let linkStatus: 'excellent' | 'good' | 'warning' | 'danger' = 'excellent';
let linkRecommendation: string | undefined;

if (detectedLinks === 0) {
    linkScore = 100; // Perfecto, sin enlaces
    linkStatus = 'excellent';
} else if (detectedLinks === 1) {
    linkScore = 75; // 25% de penalización
    linkStatus = 'warning';
    linkRecommendation = language === 'es' 
        ? 'ADVERTENCIA: 1 enlace aumenta riesgo 25%, considerar remover'
        : 'WARNING: 1 link increases risk 25%, consider removing';
} else if (detectedLinks === 2) {
    linkScore = 50; // 50% de penalización (25% x 2)
    linkStatus = 'warning';
    linkRecommendation = language === 'es' 
        ? 'ALTO RIESGO: 2 enlaces aumentan riesgo 50%, reducir a máximo 1'
        : 'HIGH RISK: 2 links increase risk 50%, reduce to maximum 1';
} else {
    linkScore = 5; // CRÍTICO - 3 o más enlaces
    linkStatus = 'danger';
    linkRecommendation = language === 'es' 
        ? `CRÍTICO: ${detectedLinks} enlaces = riesgo máximo, reducir urgentemente a máximo 1`
        : `CRITICAL: ${detectedLinks} links = maximum risk, reduce urgently to maximum 1`;
}

factors.push({
    id: 'message_links',
    name: language === 'es' ? 'Enlaces en mensaje' : 'Links in message',
    description: language === 'es' ? '0 enlaces óptimo, 1-2 riesgoso, 3+ crítico' : '0 links optimal, 1-2 risky, 3+ critical',
    weight: 22, // Peso alto pero no máximo
    currentValue: detectedLinks === 0 ? 'Sin enlaces' : `${detectedLinks} enlace${detectedLinks > 1 ? 's' : ''}`,
    optimalRange: '0',
    score: linkScore,
    status: linkStatus,
    recommendation: linkRecommendation
});

    // Factor 6: Variables dinámicas
    const hasVariables = variables.length > 0;
    const textDynamicScore = hasVariables ? 100 : 30;
    factors.push({
        id: 'text_dynamic',
        name: language === 'es' ? 'Texto dinámico' : 'Dynamic text',
        description: language === 'es' ? 'Variables personalizadas reducen flags' : 'Custom variables reduce flags',
        weight: 12,
        currentValue: hasVariables ? `${variables.length} variables` : 'Sin variables',
        optimalRange: '≥ 1',
        score: textDynamicScore,
        status: textDynamicScore > 80 ? 'excellent' : 'warning'
    });

    // Factor 7: Horarios - MÁS ESTRICTO
    const startHour = parseInt(config.timeIntervalInit?.split(':')[0] || '9');
    const endHour = parseInt(config.timeIntervalEnd?.split(':')[0] || '18');
    let scheduleScore = 100;
    if (startHour >= 9 && endHour <= 18) {
        scheduleScore = 100; // Óptimo
    } else if (startHour >= 8 && endHour <= 20) { // Horario extendido
        scheduleScore = 40; // RIESGO ALTO
    } else {
        scheduleScore = 10; // CRÍTICO
    }
    
    factors.push({
        id: 'operation_hours',
        name: language === 'es' ? 'Horario de operación' : 'Operation hours',
        description: language === 'es' ? '09:00-18:00 óptimo, fuera de eso es peligroso' : '09:00-18:00 optimal, outside is dangerous',
        weight: 10,
        currentValue: `${config.timeIntervalInit} - ${config.timeIntervalEnd}`,
        optimalRange: '09:00 - 18:00',
        score: scheduleScore,
        status: scheduleScore > 80 ? 'excellent' : scheduleScore > 30 ? 'warning' : 'danger'
    });

    // Factor 8: Días de envío - MÁS ESTRICTO
    const analyzeSendingDays = (daysString: string): {
    score: number;
    status: 'excellent' | 'good' | 'warning' | 'danger';
    recommendation?: string;
    analysis: string;
} => {
    const selectedDays = daysString.split(',').map(d => d.trim().toUpperCase());
    const totalDays = selectedDays.length;
    
    // Clasificar días
    const businessDays = ['MON', 'TUE', 'WED', 'THU', 'FRI'];
    const weekendDays = ['SAT', 'SUN'];
    
    const businessDaysSelected = selectedDays.filter(day => businessDays.includes(day));
    const weekendDaysSelected = selectedDays.filter(day => weekendDays.includes(day));
    
    const businessDayCount = businessDaysSelected.length;
    const weekendDayCount = weekendDaysSelected.length;
    
    let score = 100;
    let status: 'excellent' | 'good' | 'warning' | 'danger' = 'excellent';
    let recommendation: string | undefined;
    let analysis = '';
    
    // CASO 1: Solo días hábiles
    if (weekendDayCount === 0) {
        if (businessDayCount <= 3) {
            // ÓPTIMO: 1-3 días hábiles
            score = 100;
            status = 'excellent';
            analysis = language === 'es' 
                ? `${businessDayCount} día(s) hábil(es) - Óptimo`
                : `${businessDayCount} business day(s) - Optimal`;
        } else if (businessDayCount <= 5) {
            // RIESGO MEDIO: 4-5 días hábiles (50% del score)
            score = 50;
            status = 'warning';
            analysis = language === 'es' 
                ? `${businessDayCount} días hábiles - Riesgo medio`
                : `${businessDayCount} business days - Medium risk`;
            recommendation = language === 'es' 
                ? 'Reducir a máximo 3 días hábiles para score óptimo'
                : 'Reduce to maximum 3 business days for optimal score';
        }
    }
    // CASO 2: Incluye fines de semana
    else if (weekendDayCount > 0) {
        if (businessDayCount <= 3 && weekendDayCount === 1) {
            // ALTO RIESGO: Días hábiles + 1 fin de semana = 85% flag risk
            score = 15; // Score que resulta en 85% flag probability
            status = 'danger';
            analysis = language === 'es' 
                ? `${businessDayCount} hábiles + ${weekendDayCount} fin de semana - Alto riesgo`
                : `${businessDayCount} business + ${weekendDayCount} weekend - High risk`;
            recommendation = language === 'es' 
                ? 'PELIGRO: Evitar envíos en fines de semana (85% riesgo flag)'
                : 'DANGER: Avoid weekend sends (85% flag risk)';
        } else if (weekendDayCount >= 2 || (businessDayCount > 3 && weekendDayCount >= 1)) {
            // CRÍTICO: Múltiples fines de semana o muchos días totales
            score = 0;
            status = 'danger';
            analysis = language === 'es' 
                ? `${businessDayCount} hábiles + ${weekendDayCount} fines de semana - CRÍTICO`
                : `${businessDayCount} business + ${weekendDayCount} weekends - CRITICAL`;
            recommendation = language === 'es' 
                ? 'CRÍTICO: Remover todos los fines de semana inmediatamente'
                : 'CRITICAL: Remove all weekends immediately';
        }
    }
    
    // CASO 3: Solo fines de semana (muy raro pero posible)
    if (businessDayCount === 0 && weekendDayCount > 0) {
        if (weekendDayCount === 1) {
            // ALTO RIESGO: Solo 1 día festivo
            score = 15; // 85% flag risk
            status = 'danger';
            analysis = language === 'es' 
                ? 'Solo fines de semana - Alto riesgo'
                : 'Weekend only - High risk';
            recommendation = language === 'es' 
                ? 'PELIGRO: Cambiar a días hábiles (85% riesgo flag)'
                : 'DANGER: Change to business days (85% flag risk)';
        } else {
            // CRÍTICO: Múltiples días festivos
            score = 0;
            status = 'danger';
            analysis = language === 'es' 
                ? 'Solo fines de semana múltiples - CRÍTICO'
                : 'Multiple weekends only - CRITICAL';
            recommendation = language === 'es' 
                ? 'CRÍTICO: Cambiar completamente a días hábiles'
                : 'CRITICAL: Change completely to business days';
        }
    }
    
    return { score, status, recommendation, analysis };
};
const dayAnalysis = analyzeSendingDays(config.daysInterval || 'MON,TUE,WED,THU,FRI');

factors.push({
    id: 'sending_days',
    name: language === 'es' ? 'Días de envío' : 'Sending days',
    description: language === 'es' ? '≤3 días hábiles óptimo, fines de semana 85% riesgo, múltiples crítico' : '≤3 business days optimal, weekends 85% risk, multiple critical',
    weight: 12, // Aumentar peso porque es importante
    currentValue: dayAnalysis.analysis,
    optimalRange: language === 'es' ? '1-3 días hábiles' : '1-3 business days',
    score: dayAnalysis.score,
    status: dayAnalysis.status,
    recommendation: dayAnalysis.recommendation
});

    // Factor 9: Primera respuesta IA
    const firstResponseScore = firstResponseConfig.enabled ? 100 : 60;
    factors.push({
        id: 'first_response',
        name: language === 'es' ? 'Primera respuesta IA' : 'AI first response',
        description: language === 'es' ? 'IA mejora la interacción natural' : 'AI improves natural interaction',
        weight: 8,
        currentValue: firstResponseConfig.enabled ? 'Habilitado' : 'Deshabilitado',
        optimalRange: language === 'es' ? 'Habilitado' : 'Enabled',
        score: firstResponseScore,
        status: firstResponseScore > 80 ? 'excellent' : 'warning'
    });

    // Factor 10: Estado de WhatsApp
    const whatsappReadyScore = whatsAppStatus?.ready ? 100 : 
                              whatsAppStatus?.envExists ? 50 : 20;
    factors.push({
        id: 'whatsapp_config',
        name: language === 'es' ? 'Configuración WhatsApp' : 'WhatsApp configuration',
        description: language === 'es' ? 'Estado del archivo .env' : '.env file status',
        weight: 10,
        currentValue: whatsAppStatus?.ready ? 'Completo' : 'Incompleto',
        optimalRange: language === 'es' ? 'Completo' : 'Complete',
        score: whatsappReadyScore,
        status: whatsappReadyScore > 80 ? 'excellent' : whatsappReadyScore > 40 ? 'warning' : 'danger'
    });

    // CÁLCULO ESTRICTO CON PENALIZACIONES SEVERAS
    /*const totalWeight = factors.reduce((sum, factor) => sum + factor.weight, 0);
    let weightedScore = factors.reduce((sum, factor) => sum + (factor.score * factor.weight), 0) / totalWeight;
    
    // PENALIZACIONES ADICIONALES POR COMBINACIONES PELIGROSAS
    const criticalFactors = factors.filter(f => f.score <= 15).length;
    if (criticalFactors >= 2) {
        weightedScore = Math.min(weightedScore, 10); // Múltiples factores críticos = MÁXIMO 10
    } else if (criticalFactors >= 1) {
        weightedScore = Math.min(weightedScore, 25); // Un factor crítico = MÁXIMO 25
    } */
   const totalWeight = factors.reduce((sum, factor) => sum + factor.weight, 0);
    const weightedScore = factors.reduce((sum, factor) => sum + (factor.score * factor.weight), 0) / totalWeight;

    
    // NIVELES DE RIESGO MÁS ESTRICTOS
    /*let riskLevel: 'low' | 'medium' | 'high' | 'critical';
    let flagProbability: number;
    
    if (weightedScore >= 90) {
        riskLevel = 'low';
        flagProbability = 5;
    } else if (weightedScore >= 70) {
        riskLevel = 'medium';
        flagProbability = 25;
    } else if (weightedScore >= 40) {
        riskLevel = 'high';
        flagProbability = 85; // 85% como pediste
    } else {
        riskLevel = 'critical';
        flagProbability = 95; // CRÍTICO
    } */
    let riskLevel: 'low' | 'medium' | 'high' | 'critical';
    let flagProbability: number;
    
    if (weightedScore >= 80) {
        riskLevel = 'low';
        flagProbability = Math.max(5, Math.round((100 - weightedScore) * 0.5)); // 5-10%
    } else if (weightedScore >= 60) {
        riskLevel = 'medium';
        flagProbability = Math.max(15, Math.round((100 - weightedScore) * 0.8)); // 15-32%
    } else if (weightedScore >= 40) {
        riskLevel = 'high';
        flagProbability = Math.max(40, Math.round((100 - weightedScore) * 1.2)); // 40-72%
    } else {
        riskLevel = 'critical';
        flagProbability = Math.max(75, Math.round((100 - weightedScore) * 1.5)); // 75-90%
    }
    // Recomendaciones críticas
    /*const recommendations: string[] = [];
    const criticalIssues = factors.filter(f => f.score <= 15 && f.recommendation);
    
    if (criticalIssues.length > 0) {
        recommendations.push(language === 'es' ? '🚨 ACCIÓN INMEDIATA REQUERIDA:' : '🚨 IMMEDIATE ACTION REQUIRED:');
        criticalIssues.forEach(issue => {
            if (issue.recommendation) recommendations.push(issue.recommendation);
        });
    } else {
        const warnings = factors.filter(f => f.score <= 40 && f.recommendation);
        warnings.forEach(warning => {
            if (warning.recommendation) recommendations.push(warning.recommendation);
        });
    }

    if (recommendations.length === 0) {
        recommendations.push(language === 'es' 
            ? 'Configuración segura para evitar flags'
            : 'Safe configuration to avoid flags');
    } */
    const recommendations: string[] = [];
    
    // Ordenar factores por puntuación (peores primero)
    const sortedFactors = [...factors].sort((a, b) => a.score - b.score);
    
    // Tomar los 3 factores con peor puntuación que tengan recomendaciones
    const topIssues = sortedFactors.filter(f => f.recommendation).slice(0, 3);
    
    if (topIssues.length > 0) {
        if (weightedScore < 40) {
            recommendations.push(language === 'es' ? '🚨 MEJORAS URGENTES:' : '🚨 URGENT IMPROVEMENTS:');
        } else if (weightedScore < 60) {
            recommendations.push(language === 'es' ? '⚠️ MEJORAS RECOMENDADAS:' : '⚠️ RECOMMENDED IMPROVEMENTS:');
        } else {
            recommendations.push(language === 'es' ? 'ℹ️ OPTIMIZACIONES:' : 'ℹ️ OPTIMIZATIONS:');
        }
        
        topIssues.forEach(issue => {
            if (issue.recommendation) recommendations.push(issue.recommendation);
        });
    } else {
        recommendations.push(language === 'es' 
            ? '✅ Configuración excelente para evitar flags'
            : '✅ Excellent configuration to avoid flags');
    }

    return {
        overallScore: Math.round(weightedScore),
        riskLevel,
        factors,
        recommendations,
        flagProbability
    };
};

    const handleRiskMeterClick = () => {
        setIsCalculatingRisk(true);
        setTimeout(() => {
            const assessment = calculateRiskAssessment();
            setRiskAssessment(assessment);
            setIsCalculatingRisk(false);
            setShowRiskMeter(true);
        }, 500);
    };

    const handleDragStart = (e: React.MouseEvent) => {
        setIsDragging(true);
        const rect = e.currentTarget.getBoundingClientRect();
        const offsetX = e.clientX - rect.left;
        const offsetY = e.clientY - rect.top;

        const handleDrag = (e: MouseEvent) => {
            const newX = e.clientX - offsetX;
            const newY = e.clientY - offsetY;
            
            // Limitar a los bordes de la ventana
            const maxX = window.innerWidth - 120;
            const maxY = window.innerHeight - 120;
            
            setDragPosition({
                x: Math.max(0, Math.min(newX, maxX)),
                y: Math.max(0, Math.min(newY, maxY))
            });
        };

        const handleDragEnd = () => {
            setIsDragging(false);
            document.removeEventListener('mousemove', handleDrag);
            document.removeEventListener('mouseup', handleDragEnd);
        };

        document.addEventListener('mousemove', handleDrag);
        document.addEventListener('mouseup', handleDragEnd);
    };

const getRiskColor = (score: number): string => {
    if (score >= 90) return '#22c55e'; // verde - excelente
    if (score >= 70) return '#eab308'; // amarillo - medio
    if (score >= 40) return '#f97316'; // naranja - alto (85% flag probability)
    return '#dc2626'; // rojo oscuro - crítico (95% flag probability)
};

    const getRiskColorClass = (riskLevel: string): string => {
        switch (riskLevel) {
            case 'low': return 'success';
            case 'medium': return 'warning';
            case 'high': return 'danger';
            case 'critical': return 'danger';
            default: return 'info';
        }
    };

// ================ FUNCIONES PARA PRIMERA RESPUESTA ================

const cancelProcessing = () => {
    setIsCancelling(true);
    
    // Detener el procesamiento
    setIsProcessing(false);
    setProcessingProgress(0);
    
    // Limpiar todos los datos
    resetFormState();
    
    // Mostrar toast de cancelación
    showInfoToast(
        language === 'es' ? 'Procesamiento cancelado' : 'Processing cancelled',
        language === 'es' ? 'Todos los datos han sido limpiados exitosamente' : 'All data has been cleaned successfully'
    );
    
    setTimeout(() => {
        setIsCancelling(false);
    }, 1000);
};

const loadFirstResponseStats = async () => {
    try {
        const token = getHatateToken();
        if (!token) return;
        
        const response = await axios.get('https://backend.anubisai.net/api/whatsapp/bot/first-response/stats', {
            headers: {
                'Authorization': token,
                'x-api-key': API_KEY,
                'Content-Type': 'application/json'
            },
            timeout: 5000
        });
        
        if (response.status === 200 && response.data.success) {
            // Obtener configuración actual desde stats
            setFirstResponseConfig({
                enabled: response.data.enabled || false,
                context: response.data.context || 'marketing_general'
            });
            
            // Obtener estadísticas
            setFirstResponseStats(response.data.stats || {});
        }
    } catch (error) {
        console.error('Error loading first response stats:', error);
    }
};

const loadFirstResponseContexts = async () => {
    try {
        const token = getHatateToken();
        if (!token) return;
        
        const response = await axios.get('https://backend.anubisai.net/api/whatsapp/bot/first-response/contexts', {
            headers: {
                'Authorization': token,
                'x-api-key': API_KEY,
                'Content-Type': 'application/json'
            },
            timeout: 5000
        });
        
        if (response.status === 200 && response.data.success) {
            setFirstResponseContexts(response.data.contexts || []);
        }
    } catch (error) {
        console.error('Error loading first response contexts:', error);
        // Usar contextos por defecto si falla
        const defaultContexts: FirstResponseContext[] = [
            { key: 'marketing_general', name: 'Marketing General', description: 'Respuestas generales de marketing' },
            { key: 'inmobiliario', name: 'Inmobiliario', description: 'Respuestas para sector inmobiliario' },
            { key: 'educativo', name: 'Educativo', description: 'Respuestas para sector educativo' },
            { key: 'ventas_consultoria', name: 'Ventas y Consultoría', description: 'Respuestas para ventas y consultoría' }
        ];
        setFirstResponseContexts(defaultContexts);
    }
};

const saveFirstResponseConfig = async () => {
    setIsSavingFirstResponse(true);
    setError(null);
    setSuccess(null);
    
    try {
        const token = getHatateToken();
        if (!token) {
            throw new Error('Token de autenticación no disponible');
        }
        
        // ✅ INCLUIR processName COMO REQUIERE EL ENDPOINT
        const payload = {
            enabled: firstResponseConfig.enabled,
            context: firstResponseConfig.context,
            processName: user?._id || user?.email || 'default_process'
        };
        
        console.log('Saving first response config:', payload);
        
        const response = await axios.post('https://backend.anubisai.net/api/whatsapp/bot/first-response/toggle', payload, {
            headers: {
                'Authorization': token,
                'x-api-key': API_KEY,
                'Content-Type': 'application/json'
            },
            timeout: 15000
        });
        
        if (response.status === 200 && response.data.success) {
            showSuccessToast(
        language === 'es' ? 'Primera Respuesta' : 'First Response',
        language === 'es' 
            ? 'Configuración guardada correctamente'
            : 'Configuration saved successfully'
    );
            triggerRiskUpdate();
            // Recargar estadísticas
            setTimeout(() => {
                loadFirstResponseStats();
            }, 1000);
            
        }
    } catch (error: any) {
        console.error('Error saving first response config:', error);
        setError(language === 'es'
            ? `Error al guardar configuración: ${error.response?.data?.message || error.message}`
            : `Error saving configuration: ${error.response?.data?.message || error.message}`);
    } finally {
        setIsSavingFirstResponse(false);
    }
};

const testFirstResponse = async () => {
    if (!firstResponseConfig.enabled) {
        setError(language === 'es'
            ? 'Debe activar la Primera Respuesta antes de hacer una prueba'
            : 'You must enable First Response before testing');
        return;
    }
    
    setError(null);
    setSuccess(null);
    
    try {
        const token = getHatateToken();
        if (!token) {
            throw new Error('Token de autenticación no disponible');
        }
        
        const testPayload = {
            message: 'Hola, estoy interesado en sus servicios',
            contactId: `test_contact_${Date.now()}`,
            context: firstResponseConfig.context,
            sendToUser: false
        };
        
        const response = await axios.post('https://backend.anubisai.net/api/whatsapp/bot/first-response/test', testPayload, {
            headers: {
                'Authorization': token,
                'x-api-key': API_KEY,
                'Content-Type': 'application/json'
            },
            timeout: 20000
        });
        
        if (response.status === 200 && response.data.success) {
                 showSuccessToast(
    language === 'es' ? 'Éxito' : 'Success',
    language === 'es' 
        ? 'Prueba exitosa. Respuesta generada correctamente.'
        : 'Test successful. Response generated correctly.'
);
         
        }
    } catch (error: any) {
        console.error('Error testing first response:', error);
        showErrorToast(
    language === 'es' ? 'Error' : 'Error',
    language === 'es' 
        ? `Error al probar Primera Respuesta: ${error.response?.data?.message || error.message}`
        : `Error testing First Response: ${error.response?.data?.message || error.message}`
);
       
    }
};

// ================ CARGAR DATOS AL MONTAR COMPONENTE ================
// Agregar esto al useEffect existente que se ejecuta al cambiar activeTab

useEffect(() => {
    if (activeTab === 'config') {
        loadWhatsAppStatus();
        loadFirstResponseStats(); // Esto carga tanto stats como config actual
        loadFirstResponseContexts();
    }
}, [activeTab]);
    
    // ================ EFECTOS ================
    const now = new Date();
const months = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];
const currentMonth = months[now.getMonth()];
const currentYear = now.getFullYear();
const currentDay = now.getDay()+1;

    useEffect(() => {
        loadMockConfiguration();
        loadMockHistory();
        // ✨ CARGAR ESTADO DE WHATSAPP
        loadWhatsAppStatus();
        setVariables([
            { id: '1', name: 'fecha_inicio', value: `${currentDay+7} de ${currentMonth} ${currentYear}` },
            { id: '2', name: 'fecha_fin', value: `XX de ${currentMonth} ${currentYear}` },
            { id: '3', name: 'docentes', value: 'Dr. Juan Pérez y Dra. María González' },
            { id: '4', name: 'enlace_curso', value: `https://${companyDomain}/curso-info` },
            { id: '5', name: 'precio', value: '150000' }
        ]);
    }, [companyNameLower]);

    // Actualizar mensaje por defecto cuando cambie la company
    useEffect(() => {
        setCustomMessage(`Hola [nombre], te saludamos desde ${companyName} ya que has consultado el curso [curso].\n\nLos detalles del curso son:\n📅 Clases desde [fecha_inicio] hasta [fecha_fin]\n👨‍🏫 Docentes: [docentes]\n🔗 Link de información: [enlace_curso]\n💰 Precio: [precio]\n\n¡Esperamos tu participación! 🎓`);
    }, [companyName]);

    useEffect(() => {
        if (historyRefreshInterval) {
            clearInterval(historyRefreshInterval);
        }
        
        if (activeTab === 'history') {
            const interval = setInterval(() => {
                loadMockHistory();
            }, 30000);
            
            setHistoryRefreshInterval(interval);
            
            return () => {
                clearInterval(interval);
            };
        }
    }, [activeTab]);

    // ✨ CARGAR ESTADO DE WHATSAPP CUANDO SE ACTIVE LA PESTAÑA DE CONFIGURACIÓN
    useEffect(() => {
        if (activeTab === 'config') {
            loadWhatsAppStatus();
        }
    }, [activeTab]);
    
    // ================ FUNCIONES DE FORMATEO MONETARIO ================
    
    const isMonetaryVariable = (variableName: string): boolean => {
        if (!variableName || typeof variableName !== 'string') return false;
        
        const monetaryTerms = [
            'precio', 'cost', 'costo', 'total', 'valor', 'monto', 
            'tarifa', 'fee', 'cuota', 'pago', 'payment', 'amount',
            'cotización', 'cotizacion', 'presupuesto', 'budget',
            'descuento', 'discount', 'rebaja', 'oferta', 'promocion',
            'matricula', 'mensualidad', 'anualidad', 'inscripcion',
            'arancel', 'honorario', 'comision', 'commission'
        ];
        
        const lowerName = variableName.toLowerCase();
        return monetaryTerms.some(term => lowerName.includes(term));
    };

    const formatMonetaryValue = (value: string | number): string => {
        if (!value && value !== 0) return String(value);
        
        let stringValue = String(value).trim();
        
        if (stringValue.includes('CLP') || stringValue.includes('USD')) {
            return stringValue;
        }
        
        const numbersOnly = stringValue.replace(/[^\d.,]/g, '');
        
        if (!numbersOnly) return String(value);
        
        let numericValue;
        try {
            if (numbersOnly.includes(',')) {
                const parts = numbersOnly.split(',');
                if (parts.length === 2) {
                    const integerPart = parts[0].replace(/\./g, '');
                    const decimalPart = parts[1];
                    numericValue = parseFloat(`${integerPart}.${decimalPart}`);
                } else {
                    numericValue = parseFloat(numbersOnly.replace(/,/g, ''));
                }
            } else {
                const dotCount = (numbersOnly.match(/\./g) || []).length;
                if (dotCount === 1) {
                    const parts = numbersOnly.split('.');
                    if (parts[1].length <= 2) {
                        numericValue = parseFloat(numbersOnly);
                    } else {
                        numericValue = parseFloat(numbersOnly.replace(/\./g, ''));
                    }
                } else {
                    numericValue = parseFloat(numbersOnly.replace(/\./g, ''));
                }
            }
            
            if (isNaN(numericValue)) {
                numericValue = parseInt(numbersOnly.replace(/[^\d]/g, ''));
            }
            
            if (isNaN(numericValue)) return String(value);
            
        } catch (error) {
            console.warn('Error formatting monetary value:', error);
            return String(value);
        }
        
        const formatted = new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(numericValue);
        
        return formatted;
    };

    // ================ FUNCIONES DE VALIDACIÓN DE TELÉFONOS ================
    
    const validateAndCorrectPhone = (phone: string): { corrected: string; needsCorrection: boolean; issueType?: string } => {
        if (!phone) {
            return { corrected: '', needsCorrection: false };
        }
        
        let cleanPhone = phone.replace(/[^\d+]/g, '');
        
        if (!cleanPhone.startsWith('+')) {
            if (cleanPhone.startsWith('56')) {
                cleanPhone = '+' + cleanPhone;
            } else if (cleanPhone.length >= 8) {
                cleanPhone = '+56' + cleanPhone;
            } else {
                return { corrected: phone, needsCorrection: true, issueType: 'invalid_format' };
            }
        }
        
        const internationalPhoneRegex = /^\+\d{7,15}$/;
        
        if (!internationalPhoneRegex.test(cleanPhone)) {
            return { corrected: phone, needsCorrection: true, issueType: 'invalid_format' };
        }
        
        if (cleanPhone.startsWith('+56')) {
            const chilePhoneRegex = /^\+56\d{8,9}$/;
            if (!chilePhoneRegex.test(cleanPhone)) {
                const numberPart = cleanPhone.substring(3);
                if (numberPart.length < 8) {
                    return { corrected: phone, needsCorrection: true, issueType: 'incomplete' };
                } else if (numberPart.length === 8 || numberPart.length === 9) {
                    return { corrected: cleanPhone, needsCorrection: phone !== cleanPhone };
                } else {
                    cleanPhone = '+56' + numberPart.substring(0, 9);
                    return { corrected: cleanPhone, needsCorrection: true, issueType: 'invalid_format' };
                }
            }
        }
        
        return { corrected: cleanPhone, needsCorrection: phone !== cleanPhone };
    };

    const detectDuplicates = (contacts: any[]): DuplicateContact[] => {
        const phoneMap = new Map<string, number>();
        const duplicates: DuplicateContact[] = [];
        
        contacts.forEach((contact, index) => {
            if (contact && contact.telefono) {
                const normalizedPhone = contact.telefono.replace(/[^\d+]/g, '');
                
                if (phoneMap.has(normalizedPhone)) {
                    duplicates.push({
                        rowIndex: index,
                        phone: contact.telefono,
                        name: contact.nombre || 'Sin nombre',
                        originalRowIndex: phoneMap.get(normalizedPhone)!
                    });
                } else {
                    phoneMap.set(normalizedPhone, index);
                }
            }
        });
        
        return duplicates;
    };
    
    // ================ FUNCIÓN EASTER EGG TOASTY ================
    
const triggerToastyEasterEgg = () => {
    if (showToasty) return;
    
    try {
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        
        const createToastySound = () => {
            const osc1 = audioContext.createOscillator();
            const gain1 = audioContext.createGain();
            osc1.connect(gain1);
            gain1.connect(audioContext.destination);
            
            osc1.frequency.setValueAtTime(600, audioContext.currentTime);
            osc1.frequency.setValueAtTime(500, audioContext.currentTime + 0.1);
            gain1.gain.setValueAtTime(0.3, audioContext.currentTime);
            gain1.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
            
            osc1.start(audioContext.currentTime);
            osc1.stop(audioContext.currentTime + 0.2);
            
            setTimeout(() => {
                const osc2 = audioContext.createOscillator();
                const gain2 = audioContext.createGain();
                osc2.connect(gain2);
                gain2.connect(audioContext.destination);
                
                osc2.frequency.setValueAtTime(400, audioContext.currentTime);
                osc2.frequency.setValueAtTime(300, audioContext.currentTime + 0.1);
                gain2.gain.setValueAtTime(0.4, audioContext.currentTime);
                gain2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);
                
                osc2.start(audioContext.currentTime);
                osc2.stop(audioContext.currentTime + 0.15);
            }, 150);
        };
        
        createToastySound();
        
    } catch (e) {
        console.log('Audio context not available');
    }
    
    setShowToasty(true);
    
    setTimeout(() => {
        setShowToasty(false);
    }, 1800);
};

    // ================ ✨ NUEVAS FUNCIONES PARA WHATSAPP ================
    
    const loadWhatsAppStatus = async () => {
        setIsLoadingWhatsApp(true);
        setError(null);
        setSuccess(null);
        
        try {
            const token = getHatateToken();
            if (!token) {
                throw new Error('Token de autenticación no disponible');
            }
            
            const response = await axios.get('https://backend.anubisai.net/api/files/whatsapp-status', {
                headers: {
                    'Authorization': token,
                    'Content-Type': 'application/json'
                },
                timeout: 10000
            });
            
            if (response.status === 200 && response.data) {
                const status = response.data;
                setWhatsAppStatus(status);
                triggerRiskUpdate();
                // Actualizar configuración con datos del backend si existen
                if (status.schedule) {
                    setConfig(prev => ({
                        ...prev,
                        timeIntervalInit: status.schedule.timeIntervalInit || prev.timeIntervalInit,
                        timeIntervalEnd: status.schedule.timeIntervalEnd || prev.timeIntervalEnd,
                        daysInterval: status.schedule.daysInterval ? status.schedule.daysInterval.join(',') : prev.daysInterval
                    }));
                }
                
                console.log('WhatsApp status loaded:', status);
            }
        } catch (error: any) {
            console.error('Error loading WhatsApp status:', error);
            setError(language === 'es'
                ? `Error al cargar estado de WhatsApp: ${error.response?.data?.message || error.message}`
                : `Error loading WhatsApp status: ${error.response?.data?.message || error.message}`);
        } finally {
            setIsLoadingWhatsApp(false);
        }
    };
    
    const saveScheduleConfiguration = async () => {
        setIsSavingSchedule(true);
        setError(null);
        setSuccess(null);
        
        try {
            const token = getHatateToken();
            if (!token) {
                throw new Error('Token de autenticación no disponible');
            }
            
            const payload = {
                timeIntervalInit: config.timeIntervalInit,
                timeIntervalEnd: config.timeIntervalEnd,
                daysInterval: config.daysInterval
            };
            
            console.log('Saving schedule configuration:', payload);
            
            // Si existe configuración, usar PATCH para actualizar solo horarios
            // Si no existe, usar POST para crear archivo completo
            const endpoint = whatsAppStatus?.envExists 
                ? 'https://backend.anubisai.net/api/files/update-schedule'
                : 'https://backend.anubisai.net/api/files/create-env';
                
            const method = whatsAppStatus?.envExists ? 'patch' : 'post';
            
            const response = await axios[method](endpoint, payload, {
                headers: {
                    'Authorization': token,
                    'Content-Type': 'application/json'
                },
                timeout: 15000
            });
            
            if (response.status === 200 || response.status === 201) {
     showSuccessToast(
    language === 'es' ? 'Éxito' : 'Success',
    language === 'es' 
        ? 'Configuración de horarios guardada correctamente'
        : 'Schedule configuration saved successfully'
);
                triggerRiskUpdate();
                // Recargar estado de WhatsApp
                setTimeout(() => {
                    loadWhatsAppStatus();
                }, 1000);
                
                console.log('Schedule configuration saved:', response.data);
            }
        } catch (error: any) {
            console.error('Error saving schedule configuration:', error);
            setError(language === 'es'
                ? `Error al guardar configuración: ${error.response?.data?.message || error.message}`
                : `Error saving configuration: ${error.response?.data?.message || error.message}`);
        } finally {
            setIsSavingSchedule(false);
        }
    };
    
    // ================ FUNCIONES DE LIMPIEZA ================
    
    const resetFormState = () => {
        setUploadedFile(null);
        setProcessingProgress(0);
        setPreviewData([]);
        setValidationResult(null);
        setError(null);
        setSuccess(null);
        setProcessedData(null);
        setContactCount(0);
        setProcessingStats(null);
        
        const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
        if (fileInput) {
            fileInput.value = '';
        }
    };

    const handleSuccessModalClose = () => {
        setShowSuccessModal(false);
        setSuccessModalData(null);
        resetFormState();
    };
    
    // ================ FUNCIONES DE CONFIGURACIÓN ================
    
    const loadMockConfiguration = () => {
        setIsLoading(true);
        
        setTimeout(() => {
            setConfig({
                enabled: true,
                autoDownload: false,
                lastUpdated: Date.now(),
                // ✨ VALORES POR DEFECTO
                timeIntervalInit: '09:00',
                timeIntervalEnd: '18:00',
                daysInterval: 'MON,TUE,WED,THU,FRI'
            });
            setIsLoading(false);
        }, 500);
    };
    
    const saveConfiguration = () => {
        setIsSaving(true);
        setError(null);
        setSuccess(null);
        
        setTimeout(() => {
            setConfig({
                ...config,
                lastUpdated: Date.now()
            });
            
showSuccessToast(
    language === 'es' ? 'Configuración General' : 'General Configuration',
    language === 'es' 
        ? 'Configuración guardada correctamente'
        : 'Configuration saved successfully'
);
            triggerRiskUpdate();
            setTimeout(() => {
                setSuccess(null);
            }, 3000);
            
            setIsSaving(false);
        }, 1000);
    };
    
    // ================ FUNCIONES DE HISTORIAL ================
    
    const loadMockHistory = () => {
        if (activeTab !== 'history' && fileHistory.length > 0) return;
        
        setIsLoading(activeTab === 'history');
        
        setTimeout(() => {
            const mockHistory: FileHistoryItem[] = [
                {
                    id: 'proc_' + Date.now(),
                    originalName: `${companyNameLower}_prueba.xlsx`,
                    fileName: `${companyNameLower}_prueba.csv`,
                    processedDate: new Date().toISOString(),
                    status: 'success',
                    contactCount: 45,
                    validContactCount: 45,
                    totalRowsProcessed: 47,
                    outputFormat: ['csv'],
                    fileSize: 15420,
                    processingTimeMs: 2340,
                    userId: user?._id || 'user_123',
                    serverPath: `/tmp/database/${user._id}/${companyNameLower}_prueba.csv`,
                    correctedPhones: 3,
                    duplicatesRemoved: 2
                },
                {
                    id: 'proc_' + (Date.now() - 1000),
                    originalName: 'anubis_test.xlsx',
                    fileName: `${companyNameLower}_anubis.csv`,
                    processedDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
                    status: 'success',
                    contactCount: 32,
                    validContactCount: 30,
                    totalRowsProcessed: 35,
                    outputFormat: ['csv'],
                    fileSize: 12800,
                    processingTimeMs: 1890,
                    userId: user?._id || 'user_123',
                    serverPath: `/tmp/database/${user._id}/${companyNameLower}_anubis_test.csv`,
                    correctedPhones: 1,
                    duplicatesRemoved: 0
                }
            ];
            
            setFileHistory(mockHistory);
            setTotalFiles(mockHistory.length);
            setIsLoading(false);
        }, 500);
    };

    const addToHistory = (historyItem: FileHistoryItem) => {
        setFileHistory(prev => [historyItem, ...prev]);
        setTotalFiles(prev => prev + 1);
    };
    
    const formatDate = (dateString: string) => {
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (e) {
            return dateString;
        }
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatProcessingTime = (ms: number): string => {
        if (ms < 1000) return `${ms}ms`;
        const seconds = Math.floor(ms / 1000);
        const remainingMs = ms % 1000;
        return `${seconds}.${Math.floor(remainingMs / 100)}s`;
    };

    // ================ FUNCIONES DE VARIABLES ================

    const addVariable = () => {
        const newId = Date.now().toString();
        setVariables([...variables, { id: newId, name: '', value: '' }]);
    };

    const updateVariable = (id: string, field: 'name' | 'value', newValue: string) => {
        setVariables(variables.map(variable => 
            variable.id === id ? { ...variable, [field]: newValue } : variable
        ));
        
        if (newValue.toLowerCase().includes('toasty')) {
            triggerToastyEasterEgg();
        }
    };

    const removeVariable = (id: string) => {
        setVariables(variables.filter(variable => variable.id !== id));
    };

    const replaceVariablesInMessage = (message: string, contactData: any): string => {
        let processedMessage = message;
        
        processedMessage = processedMessage.replace(/\[nombre\]/g, contactData.nombre || '');
        processedMessage = processedMessage.replace(/\[curso\]/g, contactData.curso || '');
        
        variables.forEach(variable => {
            if (variable.name && variable.value) {
                const finalValue = isMonetaryVariable(variable.name) 
                    ? formatMonetaryValue(variable.value)
                    : variable.value;
                
                const regex = new RegExp(`\\[${variable.name}\\]`, 'g');
                processedMessage = processedMessage.replace(regex, finalValue);
            }
        });
        
        return processedMessage;
    };
    
    // ================ FUNCIONES DE VALIDACIÓN ================
    
    const validateFileStructure = (rawData: any[]): ValidationResult => {
        const result: ValidationResult = {
            isValid: true,
            errors: [],
            warnings: [],
            totalRows: rawData.length,
            validContacts: 0,
            phoneIssues: [],
            duplicates: []
        };

        if (!rawData || rawData.length === 0) {
            result.isValid = false;
            result.errors.push(language === 'es' 
                ? 'El archivo está vacío o no contiene datos válidos'
                : 'File is empty or contains no valid data');
            return result;
        }

        if (rawData.length < 1) {
            result.isValid = false;
            // Continuación de validateFileStructure
            result.errors.push(language === 'es'
                ? 'El archivo debe contener al menos una fila de datos'
                : 'File must contain at least one row of data');
            return result;
        }

        // Procesar contactos y detectar problemas
        const processedContacts: any[] = [];
        let rowsWithInsufficientColumns = 0;
        let rowsWithColumn4Empty = 0;
        let validContactsCount = 0;

        rawData.forEach((row, index) => {
            if (!Array.isArray(row) || row.length < 4) {
                rowsWithInsufficientColumns++;
            } else {
                const column4Data = row[3];
                if (!column4Data || typeof column4Data !== 'string' || column4Data.trim() === '') {
                    rowsWithColumn4Empty++;
                } else {
                    const contactData = parseCompanyData(column4Data);
                    if (contactData && contactData.nombre && contactData.telefono) {
                        const phoneValidation = validateAndCorrectPhone(contactData.telefono);
                        
                        if (phoneValidation.needsCorrection) {
                            result.phoneIssues.push({
                                rowIndex: index + 1,
                                originalPhone: contactData.telefono,
                                correctedPhone: phoneValidation.corrected,
                                issueType: phoneValidation.issueType as any || 'invalid_format',
                                contactName: contactData.nombre
                            });
                        }
                        
                        processedContacts.push({
                            ...contactData,
                            telefono: phoneValidation.corrected,
                            rowIndex: index
                        });
                        
                        validContactsCount++;
                    }
                }
            }
        });

        result.duplicates = detectDuplicates(processedContacts);
        result.validContacts = validContactsCount;

        if (validContactsCount === 0) {
            result.isValid = false;
            result.errors.push(language === 'es'
                ? 'No se encontraron contactos válidos en el archivo'
                : 'No valid contacts found in the file');
        }

        if (rowsWithInsufficientColumns > 0) {
            result.warnings.push(language === 'es'
                ? `${rowsWithInsufficientColumns} filas no tienen suficientes columnas (mínimo 4 requeridas)`
                : `${rowsWithInsufficientColumns} rows don't have enough columns (minimum 4 required)`);
        }

        if (rowsWithColumn4Empty > 0) {
            result.warnings.push(language === 'es'
                ? `${rowsWithColumn4Empty} filas tienen la columna 4 vacía o inválida`
                : `${rowsWithColumn4Empty} rows have empty or invalid column 4`);
        }

        if (result.phoneIssues.length > 0) {
            result.warnings.push(language === 'es'
                ? `${result.phoneIssues.length} números de teléfono necesitan corrección automática`
                : `${result.phoneIssues.length} phone numbers need automatic correction`);
        }

        if (result.duplicates.length > 0) {
            result.warnings.push(language === 'es'
                ? `${result.duplicates.length} contactos duplicados serán removidos`
                : `${result.duplicates.length} duplicate contacts will be removed`);
        }

        return result;
    };

    const getValidationErrorMessage = (validation: ValidationResult): string => {
        if (language === 'es') {
            return `
**Errores encontrados en el archivo:**

${validation.errors.map(error => `• ${error}`).join('\n')}

**Formato requerido:**
• El archivo debe ser Excel (.xlsx, .xls) o CSV
• Debe tener al menos 4 columnas por fila
• La columna 4 debe contener datos en formato: CL,Nombre,email,telefono,curso
• Ejemplo: "CL,Tania,taniainzunza.to@gmail.com,+56949850335,Selectividad alimentaria..."

**Correcciones necesarias:**
1. Verificar que el archivo tenga datos en todas las filas
2. Asegurar que la columna 4 contenga la información de contacto separada por comas
3. Verificar que cada contacto tenga al menos: país,nombre,email,teléfono,curso
4. El teléfono debe incluir código de país (+56 para Chile)

${validation.warnings.length > 0 ? `**Advertencias:**\n${validation.warnings.map(warning => `• ${warning}`).join('\n')}` : ''}
            `.trim();
        } else {
            return `
**Errors found in file:**

${validation.errors.map(error => `• ${error}`).join('\n')}

**Required format:**
• File must be Excel (.xlsx, .xls) or CSV
• Must have at least 4 columns per row
• Column 4 must contain data in format: CL,Name,email,phone,course
• Example: "CL,Tania,taniainzunza.to@gmail.com,+56949850335,Food selectivity..."

**Required corrections:**
1. Verify file has data in all rows
2. Ensure column 4 contains contact information separated by commas
3. Verify each contact has at least: country,name,email,phone,course
4. Phone must include country code (+56 for Chile)

${validation.warnings.length > 0 ? `**Warnings:**\n${validation.warnings.map(warning => `• ${warning}`).join('\n')}` : ''}
            `.trim();
        }
    };
    
    // ================ FUNCIONES DE PROCESAMIENTO DE ARCHIVOS ================
    
    const parseCompanyData = (rawData: string): { nombre: string, telefono: string, curso: string } | null => {
        try {
            const parts = rawData.split(',').map(part => part.trim());
            
            if (parts.length < 4) {
                return null;
            }
            
            const nombre = parts[1] || '';
            let telefono = parts[3] || '';
            const curso = parts.slice(4).join(',').trim();
            
            if (!nombre || !telefono) {
                return null;
            }
            
            telefono = telefono.replace(/[^\d+]/g, '');
            
            return {
                nombre,
                telefono,
                curso
            };
        } catch (error) {
            console.error('Error parsing data:', error);
            return null;
        }
    };
    
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            setUploadedFile(files[0]);
            setProcessingProgress(0);
            setPreviewData([]);
            setValidationResult(null);
            setError(null);
            setSuccess(null);
            setProcessedData(null);
            setProcessingStats(null);
            
            const file = files[0];
            const reader = new FileReader();
            
            reader.onload = (event) => {
                try {
                    const extension = file.name.split('.').pop()?.toLowerCase();
                    let rawData: any[] = [];
                    
                    if (!['xlsx', 'xls', 'csv'].includes(extension || '')) {
                        setError(language === 'es'
                            ? 'Formato de archivo no soportado. Use Excel (.xlsx, .xls) o CSV'
                            : 'Unsupported file format. Use Excel (.xlsx, .xls) or CSV');
                        return;
                    }
                    
                    if (['xlsx', 'xls'].includes(extension || '')) {
                        const arrayBuffer = event.target?.result as ArrayBuffer;
                        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
                        const firstSheetName = workbook.SheetNames[0];
                        const worksheet = workbook.Sheets[firstSheetName];
                        rawData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
                    } else if (extension === 'csv') {
                        const csvContent = event.target?.result as string;
                        const parsedData = Papa.parse(csvContent, {
                            header: false,
                            skipEmptyLines: true
                        });
                        rawData = parsedData.data as any[];
                    }
                    
                    const validation = validateFileStructure(rawData);
                    setValidationResult(validation);
                    
                    if (!validation.isValid) {
                        setError(getValidationErrorMessage(validation));
                        return;
                    }
                    
                    if (validation.warnings.length > 0) {
                        setSuccess(language === 'es'
                            ? `Archivo cargado con ${validation.warnings.length} advertencia(s). Revise los detalles abajo.`
                            : `File loaded with ${validation.warnings.length} warning(s). Check details below.`);
                    }
                    
                    const processedContacts: any[] = [];
                    
                    rawData.forEach((row, index) => {
                        if (Array.isArray(row) && row.length >= 4) {
                            const rawContactData = row[3];
                            if (typeof rawContactData === 'string') {
                                const contactData = parseCompanyData(rawContactData);
                                if (contactData && contactData.telefono && contactData.nombre) {
                                    const phoneValidation = validateAndCorrectPhone(contactData.telefono);
                                    
                                    processedContacts.push({
                                        fila: index + 1,
                                        nombre: contactData.nombre,
                                        telefono: contactData.telefono,
                                        telefono_corregido: phoneValidation.corrected,
                                        necesita_correccion: phoneValidation.needsCorrection,
                                        curso: contactData.curso,
                                        mensaje_preview: 'Se generará con las variables configuradas'
                                    });
                                }
                            }
                        }
                    });
                    
                    setPreviewData(processedContacts.slice(0, 10));
                    setContactCount(validation.validContacts);
                    
                } catch (err: any) {
                    console.error('Error parsing file:', err);
                    setError(language === 'es'
                        ? `Error al procesar el archivo: ${err.message}`
                        : `Error processing file: ${err.message}`);
                }
            };
            
            reader.onerror = () => {
                setError(language === 'es'
                    ? 'Error al leer el archivo'
                    : 'Error reading file');
            };
            
            const extension = file.name.split('.').pop()?.toLowerCase();
            if (['xlsx', 'xls'].includes(extension || '')) {
                reader.readAsArrayBuffer(file);
            } else {
                reader.readAsText(file);
            }
        }
    };
    
    const showValidationModalBeforeProcessing = () => {
        if (!validationResult) return;
        
        const hasIssues = validationResult.phoneIssues.length > 0 || validationResult.duplicates.length > 0;
        
        if (hasIssues) {
            setPhoneValidationData({
                issues: validationResult.phoneIssues,
                duplicates: validationResult.duplicates,
                onConfirm: () => {
                    setShowPhoneValidationModal(false);
                    setPhoneValidationData(null);
                    processFileInternal();
                },
                onCancel: () => {
                    setShowPhoneValidationModal(false);
                    setPhoneValidationData(null);
                }
            });
            setShowPhoneValidationModal(true);
        } else {
            processFileInternal();
        }
    };
    
    const processFile = async () => {
        if (!uploadedFile) {
            setError(language === 'es'
                ? 'Debe proporcionar un archivo para procesar'
                : 'You must provide a file to process');
            return;
        }
        
        if (!customMessage.trim()) {
            setError(language === 'es'
                ? 'Debe ingresar un mensaje personalizado'
                : 'You must enter a custom message');
            return;
        }

        if (!validationResult || !validationResult.isValid) {
            setError(language === 'es'
                ? 'El archivo no ha pasado la validación. Corrija los errores antes de procesar.'
                : 'File has not passed validation. Fix errors before processing.');
            return;
        }
        
        showValidationModalBeforeProcessing();
    };

    const processFileInternal = async () => {
        setIsProcessing(true);
        setError(null);
        setSuccess(null);
        setProcessingProgress(0);
        
        const stats: ProcessingStats = {
            startTime: Date.now(),
            totalRows: 0,
            validContacts: 0,
            skippedRows: 0,
            errorRows: 0,
            correctedPhones: 0,
            duplicatesRemoved: 0
        };
        
        try {
            const reader = new FileReader();
            
            reader.onload = async (event) => {
                try {
                    const extension = uploadedFile?.name.split('.').pop()?.toLowerCase();
                    let rawData: any[] = [];
                    
                    setProcessingProgress(20);
                    
                    if (['xlsx', 'xls'].includes(extension || '')) {
                        const arrayBuffer = event.target?.result as ArrayBuffer;
                        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
                        const firstSheetName = workbook.SheetNames[0];
                        const worksheet = workbook.Sheets[firstSheetName];
                        rawData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
                    } else if (extension === 'csv') {
                        const csvContent = event.target?.result as string;
                        const parsedData = Papa.parse(csvContent, {
                            header: false,
                            skipEmptyLines: true
                        });
                        rawData = parsedData.data as any[];
                    }
                    
                    stats.totalRows = rawData.length;
                    setProcessingProgress(40);
                    
                    const processedContacts: ContactData[] = [];
                    const seenPhones = new Set<string>();
                    
                    rawData.forEach((row, index) => {
                        if (Array.isArray(row) && row.length >= 4) {
                            const rawContactData = row[3];
                            if (typeof rawContactData === 'string') {
                                const contactData = parseCompanyData(rawContactData);
                                if (contactData && contactData.telefono && contactData.nombre) {
                                    const phoneValidation = validateAndCorrectPhone(contactData.telefono);
                                    const finalPhone = phoneValidation.corrected;
                                    
                                    const normalizedPhone = finalPhone.replace(/[^\d+]/g, '');
                                    if (seenPhones.has(normalizedPhone)) {
                                        stats.duplicatesRemoved++;
                                        console.log(`Duplicado removido en fila ${index + 1}: ${contactData.nombre} - ${finalPhone}`);
                                        return;
                                    }
                                    
                                    seenPhones.add(normalizedPhone);
                                    
                                    if (phoneValidation.needsCorrection) {
                                        stats.correctedPhones++;
                                        console.log(`Teléfono corregido en fila ${index + 1}: ${contactData.telefono} -> ${finalPhone}`);
                                    }
                                    
                                    const personalizedMessage = replaceVariablesInMessage(customMessage, {
                                        ...contactData,
                                        telefono: finalPhone
                                    });
                                    
                                    processedContacts.push({
                                        nombre: contactData.nombre,
                                        telefono: finalPhone,
                                        curso: contactData.curso,
                                        mensaje: personalizedMessage
                                    });
                                    stats.validContacts++;
                                } else {
                                    stats.errorRows++;
                                }
                            } else {
                                stats.skippedRows++;
                            }
                        } else {
                            stats.skippedRows++;
                        }
                        
                        setProcessingProgress(40 + Math.floor((index / rawData.length) * 40));
                    });
                    
                    setProcessingProgress(80);
                    
                    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
                    const baseFilename = `${companyNameLower}_contactos_${timestamp}`;
                    let serverPath: string | undefined;
                    
                    try {
                        serverPath = await saveToServerTmp(processedContacts, `${baseFilename}.csv`, 'csv');
                        console.log('Archivo CSV guardado en:', serverPath);
                    } catch (saveError: any) {
                        console.error('Error guardando CSV:', saveError);
                        console.warn('Continuando sin guardar en servidor');
                    }
                    
                    stats.endTime = Date.now();
                    setProcessingStats(stats);
                    setProcessingProgress(100);
                    
                    setContactCount(processedContacts.length);
                    setProcessedData(processedContacts);
                    
                    const historyItem: FileHistoryItem = {
                        id: `proc_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
                        originalName: uploadedFile?.name || 'unknown_file',
                        fileName: `${baseFilename}.csv`,
                        processedDate: new Date().toISOString(),
                        status: 'success',
                        contactCount: processedContacts.length,
                        validContactCount: stats.validContacts,
                        totalRowsProcessed: stats.totalRows,
                        outputFormat: ['csv'],
                        fileSize: uploadedFile?.size || 0,
                        processingTimeMs: stats.endTime! - stats.startTime,
                        userId: user?._id || 'anonymous',
                        serverPath: serverPath,
                        correctedPhones: stats.correctedPhones,
                        duplicatesRemoved: stats.duplicatesRemoved
                    };
                    
                    addToHistory(historyItem);
                    
                    if (config.autoDownload) {
                        downloadCSV(processedContacts);
                    }
                    
                    setSuccessModalData({
                        fileName: uploadedFile?.name || 'archivo',
                        contactCount: processedContacts.length,
                        processingTime: formatProcessingTime(stats.endTime! - stats.startTime),
                        filePath: serverPath,
                        correctedPhones: stats.correctedPhones,
                        duplicatesRemoved: stats.duplicatesRemoved
                    });
                    setShowSuccessModal(true);
                    
                } catch (err: any) {
                    console.error('Error processing file:', err);
                    setError(language === 'es'
                        ? `Error al procesar el archivo: ${err.message}`
                        : `Error processing file: ${err.message}`);
                    
                    const errorHistoryItem: FileHistoryItem = {
                        id: `proc_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
                        originalName: uploadedFile?.name || 'unknown_file',
                        fileName: '',
                        processedDate: new Date().toISOString(),
                        status: 'error',
                        contactCount: 0,
                        validContactCount: 0,
                        totalRowsProcessed: 0,
                        outputFormat: [],
                        fileSize: uploadedFile?.size || 0,
                        processingTimeMs: Date.now() - stats.startTime,
                        userId: user?._id || 'anonymous',
                        errorMessage: err.message
                    };
                    
                    addToHistory(errorHistoryItem);
                } finally {
                    setIsProcessing(false);
                }
            };
            
            reader.onerror = () => {
                setError(language === 'es'
                    ? 'Error al leer el archivo'
                    : 'Error reading file');
                setIsProcessing(false);
            };
            
            const extension = uploadedFile?.name.split('.').pop()?.toLowerCase();
            if (['xlsx', 'xls'].includes(extension || '')) {
                reader.readAsArrayBuffer(uploadedFile!);
            } else {
                reader.readAsText(uploadedFile!);
            }
            
        } catch (err: any) {
            console.error('Error in processing:', err);
            setError(language === 'es'
                ? `Error al procesar el archivo: ${err.message}`
                : `Error processing file: ${err.message}`);
            setIsProcessing(false);
        }
    };
    
    // ================ FUNCIONES DE DESCARGA ================
    
    const downloadCSV = (data: ContactData[]) => {
        const csvContent = Papa.unparse(data, { header: false });
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        
        const a = document.createElement('a');
        a.href = url;
        a.download = `${companyNameLower}_contactos_${new Date().toISOString().slice(0,10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const saveToServerTmp = async (data: ContactData[], filename: string, format: 'csv') => {
        try {
            const content = Papa.unparse(data, { header: false });
            const contentType = 'text/csv';
            
            const token = getHatateToken();
            if (!token) {
                throw new Error('Token de autenticación no disponible');
            }
            
            const response = await axios.post('https://backend.anubisai.net/api/files/save-to-tmp', {
                filename: filename,
                content: content, 
                contentType: contentType
            }, {
                headers: {
                    'Authorization': token,
                    'Content-Type': 'application/json'
                },
                timeout: 30000
            });
            
            if (response.status === 201 && response.data.success) {
                return response.data.path;
            } else {
                throw new Error(`Error inesperado del servidor: ${response.status}`);
            }
            
        } catch (error: any) {
            console.error('Error guardando archivo en servidor:', error);
            
            if (error.response) {
                const errorMessage = error.response.data?.message || error.response.statusText || 'Error del servidor';
                throw new Error(`Error del servidor (${error.response.status}): ${errorMessage}`);
            } else if (error.request) {
                throw new Error('Error de conexión con el servidor. Verifique su conexión a internet.');
            } else {
                throw new Error(`Error interno: ${error.message}`);
            }
        }
    };

    // ✨ HELPER PARA FORMATEAR DÍAS
const formatDaysForDisplay = (daysString: string): string => {
    const dayMapping = {
        'MON': language === 'es' ? 'Lun' : 'Mon',
        'TUE': language === 'es' ? 'Mar' : 'Tue', 
        'WED': language === 'es' ? 'Mié' : 'Wed',
        'THU': language === 'es' ? 'Jue' : 'Thu',
        'FRI': language === 'es' ? 'Vie' : 'Fri',
        'SAT': language === 'es' ? 'Sáb⚠️' : 'Sat⚠️', // Indicador de riesgo
        'SUN': language === 'es' ? 'Dom⚠️' : 'Sun⚠️'  // Indicador de riesgo
    };
    
    const days = daysString.split(',')
        .map(day => dayMapping[day.trim().toUpperCase()] || day.trim())
        .join(', ');
    
    // Agregar indicador de riesgo si hay fines de semana
    const hasWeekends = daysString.toUpperCase().includes('SAT') || daysString.toUpperCase().includes('SUN');
    if (hasWeekends) {
        return `${days} ${language === 'es' ? '(¡RIESGO!)' : '(RISK!)'}`;
    }
    
    return days;
};

    // ✨ HELPER PARA OPCIONES DE DÍAS
    const getDayOptions = () => {
        const days = [
            { value: 'MON', label: language === 'es' ? 'Lunes' : 'Monday' },
            { value: 'TUE', label: language === 'es' ? 'Martes' : 'Tuesday' },
            { value: 'WED', label: language === 'es' ? 'Miércoles' : 'Wednesday' },
            { value: 'THU', label: language === 'es' ? 'Jueves' : 'Thursday' },
            { value: 'FRI', label: language === 'es' ? 'Viernes' : 'Friday' },
            { value: 'SAT', label: language === 'es' ? 'Sábado' : 'Saturday' },
            { value: 'SUN', label: language === 'es' ? 'Domingo' : 'Sunday' }
        ];
        
        return days;
    };

   // ================ RENDERIZADO DEL COMPONENTE ================ //

return(
    <>
        {/* ✨ WIDGET FLOTANTE SPEEDOMETER */}
        <div
            style={{
                position: 'fixed',
                left: dragPosition.x,
                top: dragPosition.y,
                zIndex: 1050,
                cursor: isDragging ? 'grabbing' : 'pointer',
                userSelect: 'none',
                transition: isDragging ? 'none' : 'all 0.2s ease',
                transform: isDragging ? 'scale(1.05)' : 'scale(1)'
            }}
            onMouseDown={handleDragStart}
            onClick={!isDragging ? handleRiskMeterClick : undefined}
        >
            <div
                style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: `conic-gradient(from 0deg, ${getRiskColor(riskAssessment?.overallScore || 75)} 0deg ${(riskAssessment?.overallScore || 75) * 3.6}deg, #e5e7eb ${(riskAssessment?.overallScore || 75) * 3.6}deg 360deg)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15), inset 0 0 0 8px white',
                    border: '3px solid white',
                    animation: isCalculatingRisk ? 'spin 1s linear infinite' : 'none'
                }}
            >
                <div
                    style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '50%',
                        backgroundColor: 'white',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        color: getRiskColor(riskAssessment?.overallScore || 75),
                        textAlign: 'center',
                        lineHeight: '1.1'
                    }}
                >
                    {isCalculatingRisk ? (
                        <FontAwesomeIcon icon={faSync} className="fa-spin" style={{ fontSize: '14px' }} />
                    ) : (
                        <>
                            <div style={{ fontSize: '14px' }}>{riskAssessment?.overallScore || '75'}</div>
                            <div style={{ fontSize: '8px', opacity: 0.7 }}>
                                {language === 'es' ? 'RIESGO' : 'RISK'}
                            </div>
                        </>
                    )}
                </div>
            </div>
            
            {/* Indicador de drag */}
            {!isDragging && (
                <div
                    style={{
                        position: 'absolute',
                        bottom: '-5px',
                        right: '-5px',
                        width: '20px',
                        height: '20px',
                        backgroundColor: '#6366f1',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '8px',
                        color: 'white',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
                    }}
                >
                    <FontAwesomeIcon icon={faInfoCircle} />
                </div>
            )}
        </div>

        {/* ✨ MODAL DEL RISK METER con efecto Mac */}
<Modal 
    show={showRiskMeter} 
    onHide={() => setShowRiskMeter(false)} 
    centered 
    size="xl"
    className="risk-meter-modal modal-mobile-fix"
    backdrop="static"
    style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh' }}
>
    <Modal.Header closeButton className={`bg-${getRiskColorClass(riskAssessment?.riskLevel || 'medium')} text-white`}>
        <Modal.Title className="fs-6 fs-md-5">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            {language === 'es' ? 'Medidor de Riesgo WhatsApp' : 'WhatsApp Risk Meter'}
            {riskAssessment && (
                <Badge bg="light" text="dark" className="ms-2">
                    {language === 'es' ? 'Puntuación' : 'Score'}: {riskAssessment.overallScore}/100
                </Badge>
            )}
        </Modal.Title>
    </Modal.Header>
    <Modal.Body className="p-2 p-md-3" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {riskAssessment && (
            <div>
                {/* Speedometer Principal */}
                <div className="text-center mb-3 mb-md-4">
                    {/* Version Desktop */}
                    <div 
                        style={{
                            width: '160px',
                            height: '160px',
                            borderRadius: '50%',
                            background: `conic-gradient(from 0deg, ${getRiskColor(riskAssessment.overallScore)} 0deg ${riskAssessment.overallScore * 3.6}deg, #e5e7eb ${riskAssessment.overallScore * 3.6}deg 360deg)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), inset 0 0 0 15px white',
                            border: '6px solid white'
                        }}
                        className="d-none d-md-flex"
                    >
                        <div
                            style={{
                                width: '100px',
                                height: '100px',
                                borderRadius: '50%',
                                backgroundColor: 'white',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
                            }}
                        >
                            <div style={{ fontSize: '28px', fontWeight: 'bold', color: getRiskColor(riskAssessment.overallScore) }}>
                                {riskAssessment.overallScore}
                            </div>
                            <div style={{ fontSize: '10px', color: '#6b7280', fontWeight: '600' }}>
                                {language === 'es' ? 'PUNTUACIÓN' : 'SCORE'}
                            </div>
                            <div style={{ fontSize: '8px', color: getRiskColor(riskAssessment.overallScore), fontWeight: 'bold' }}>
                                {riskAssessment.riskLevel.toUpperCase()}
                            </div>
                        </div>
                    </div>
                    
                    {/* Version Mobile */}
                    <div 
                        style={{
                            width: '120px',
                            height: '120px',
                            borderRadius: '50%',
                            background: `conic-gradient(from 0deg, ${getRiskColor(riskAssessment.overallScore)} 0deg ${riskAssessment.overallScore * 3.6}deg, #e5e7eb ${riskAssessment.overallScore * 3.6}deg 360deg)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto',
                            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1), inset 0 0 0 10px white',
                            border: '4px solid white'
                        }}
                        className="d-flex d-md-none"
                    >
                        <div
                            style={{
                                width: '80px',
                                height: '80px',
                                borderRadius: '50%',
                                backgroundColor: 'white',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
                            }}
                        >
                            <div style={{ fontSize: '20px', fontWeight: 'bold', color: getRiskColor(riskAssessment.overallScore) }}>
                                {riskAssessment.overallScore}
                            </div>
                            <div style={{ fontSize: '8px', color: '#6b7280', fontWeight: '600' }}>
                                {language === 'es' ? 'PUNTUACIÓN' : 'SCORE'}
                            </div>
                            <div style={{ fontSize: '6px', color: getRiskColor(riskAssessment.overallScore), fontWeight: 'bold' }}>
                                {riskAssessment.riskLevel.toUpperCase()}
                            </div>
                        </div>
                    </div>
                    
                    <Alert variant={`subtle-${getRiskColorClass(riskAssessment.riskLevel)}`} className="mt-3">
                        <div className="text-center">
                            <h6 className="mb-2">
                                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                                {language === 'es' ? 'Probabilidad de Flag' : 'Flag Probability'}: {riskAssessment.flagProbability}%
                            </h6>
                            <p className="mb-0">
                                {riskAssessment.riskLevel === 'low' && (language === 'es' 
                                    ? '✅ Configuración excelente. Muy bajo riesgo de flags.'
                                    : '✅ Excellent configuration. Very low risk of flags.')}
                                {riskAssessment.riskLevel === 'medium' && (language === 'es' 
                                    ? '⚠️ Configuración aceptable. Riesgo moderado de flags.'
                                    : '⚠️ Acceptable configuration. Moderate risk of flags.')}
                                {riskAssessment.riskLevel === 'high' && (language === 'es' 
                                    ? '🚨 Configuración peligrosa. Alto riesgo de flags.'
                                    : '🚨 Dangerous configuration. High risk of flags.')}
                                {riskAssessment.riskLevel === 'critical' && (language === 'es' 
                                    ? '💀 Configuración crítica. Muy alto riesgo de flags.'
                                    : '💀 Critical configuration. Very high risk of flags.')}
                            </p>
                        </div>
                    </Alert>
                </div>

                {/* Factores de Riesgo */}
                <h6 className="fs-6 fs-md-5">
                    <FontAwesomeIcon icon={faCog} className="me-2" />
                    {language === 'es' ? 'Factores de Riesgo' : 'Risk Factors'}
                </h6>
                
                <div className="row g-2 mb-3 mb-md-4">
                    {riskAssessment.factors.map((factor, index) => (
                        <div key={factor.id} className="col-12 col-md-6">
                            <div className={`card h-100 border-${
                                factor.status === 'excellent' ? 'success' :
                                factor.status === 'good' ? 'info' :
                                factor.status === 'warning' ? 'warning' : 'danger'
                            }`}>
                                <div className="card-body p-2">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <h6 className="card-title mb-0" style={{ fontSize: '0.8rem' }}>
                                            {factor.name}
                                        </h6>
                                        <Badge bg={
                                            factor.status === 'excellent' ? 'success' :
                                            factor.status === 'good' ? 'info' :
                                            factor.status === 'warning' ? 'warning' : 'danger'
                                        } className="fs-7">
                                            {factor.score}
                                        </Badge>
                                    </div>
                                    <div className="progress mb-2" style={{ height: '4px' }}>
                                        <div 
                                            className={`progress-bar bg-${
                                                factor.status === 'excellent' ? 'success' :
                                                factor.status === 'good' ? 'info' :
                                                factor.status === 'warning' ? 'warning' : 'danger'
                                            }`}
                                            style={{ width: `${factor.score}%` }}
                                        ></div>
                                    </div>
                                    <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>{factor.description}</small>
                                    <div className="mt-1">
                                        <small style={{ fontSize: '0.65rem' }}>
                                            <strong>{language === 'es' ? 'Actual' : 'Current'}:</strong> {factor.currentValue} | 
                                            <strong className="ms-1">{language === 'es' ? 'Óptimo' : 'Optimal'}:</strong> {factor.optimalRange}
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Recomendaciones */}
                <h6 className="fs-6 fs-md-5">
                    <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                    {language === 'es' ? 'Recomendaciones' : 'Recommendations'}
                </h6>
                <div className="list-group list-group-flush">
                    {riskAssessment.recommendations.map((recommendation, index) => (
                        <div key={index} className="list-group-item border-0 px-0 py-1">
                            <FontAwesomeIcon icon={faCheck} className="text-success me-2" />
                            <small style={{ fontSize: '0.8rem' }}>{recommendation}</small>
                        </div>
                    ))}
                </div>
            </div>
        )}
    </Modal.Body>
    <Modal.Footer className="p-2 p-md-3 d-flex flex-column flex-md-row gap-2">
        <Button variant="secondary" onClick={() => setShowRiskMeter(false)} className="w-100 w-md-auto" size="sm">
            <FontAwesomeIcon icon={faTimes} className="me-2" />
            {language === 'es' ? 'Cerrar' : 'Close'}
        </Button>
        <Button 
            variant="primary" 
            onClick={() => {
                const assessment = calculateRiskAssessment();
                setRiskAssessment(assessment);
            }}
            className="w-100 w-md-auto"
            size="sm"
        >
            <FontAwesomeIcon icon={faSync} className="me-2" />
            {language === 'es' ? 'Recalcular' : 'Recalculate'}
        </Button>
    </Modal.Footer>
</Modal>

        {/* Modal de Validación de Teléfonos y Duplicados */}
<Modal 
    show={showPhoneValidationModal} 
    onHide={() => setShowPhoneValidationModal(false)} 
    centered 
    size="xl"
    className="modal-mobile-fix"
    backdrop="static"
    style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh' }}
>
            <Modal.Header closeButton className="bg-warning text-dark">
                <Modal.Title className="fs-6 fs-md-5">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    {language === 'es' ? 'Problemas Detectados en el Archivo' : 'Issues Detected in File'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-2 p-md-3" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {phoneValidationData && (
                    <div>
                        {phoneValidationData.issues.length > 0 && (
                            <div className="mb-3 mb-md-4">
                                <h5 className="text-warning fs-6 fs-md-5">
                                    <FontAwesomeIcon icon={faPhone} className="me-2" />
                                    {language === 'es' ? 'Números de Teléfono con Correcciones' : 'Phone Numbers with Corrections'}
                                    <Badge bg="warning" className="ms-2">{phoneValidationData.issues.length}</Badge>
                                </h5>
                                <p className="text-muted small">
                                    {language === 'es' 
                                        ? 'Los siguientes números serán corregidos automáticamente para mantener el formato estándar +56XXXXXXXXX:'
                                        : 'The following numbers will be automatically corrected to maintain the standard format +56XXXXXXXXX:'}
                                </p>
                                
                                <div className="table-responsive" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                    <Table striped bordered size="sm" className="mb-0">
                                        <thead className="table-warning sticky-top">
                                            <tr>
                                                <th className="text-nowrap small">{language === 'es' ? 'Fila' : 'Row'}</th>
                                                <th className="small d-none d-md-table-cell">{language === 'es' ? 'Contacto' : 'Contact'}</th>
                                                <th className="text-nowrap small">{language === 'es' ? 'Original' : 'Original'}</th>
                                                <th className="text-nowrap small">{language === 'es' ? 'Corregido' : 'Corrected'}</th>
                                                <th className="small d-none d-lg-table-cell">{language === 'es' ? 'Tipo' : 'Type'}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {phoneValidationData.issues.map((issue, index) => (
                                                <tr key={index} className="table-warning">
                                                    <td>
                                                        <Badge bg="secondary" className="small">{issue.rowIndex}</Badge>
                                                    </td>
                                                    <td className="d-none d-md-table-cell">
                                                        <div className="text-truncate" style={{ maxWidth: '100px' }}>
                                                            <strong className="small">{issue.contactName}</strong>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <code className="text-danger bg-light p-1 small d-block text-truncate" style={{ maxWidth: '100px' }}>
                                                            {issue.originalPhone}
                                                        </code>
                                                    </td>
                                                    <td>
                                                        <code className="text-success bg-light p-1 small d-block text-truncate" style={{ maxWidth: '100px' }}>
                                                            {issue.correctedPhone}
                                                        </code>
                                                    </td>
                                                    <td className="d-none d-lg-table-cell">
                                                        <Badge bg={
                                                            issue.issueType === 'incomplete' ? 'danger' :
                                                            issue.issueType === 'missing_country_code' ? 'warning' : 'info'
                                                        } className="small">
                                                            {issue.issueType === 'incomplete' 
                                                                ? (language === 'es' ? 'Incompleto' : 'Incomplete')
                                                                : issue.issueType === 'missing_country_code'
                                                                ? (language === 'es' ? 'Sin código' : 'No code')
                                                                : (language === 'es' ? 'Formato' : 'Format')
                                                            }
                                                        </Badge>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </div>
                        )}
                        
                        {phoneValidationData.duplicates.length > 0 && (
                            <div className="mb-3 mb-md-4">
                                <h5 className="text-info fs-6 fs-md-5">
                                    <FontAwesomeIcon icon={faCopy} className="me-2" />
                                    {language === 'es' ? 'Contactos Duplicados' : 'Duplicate Contacts'}
                                    <Badge bg="info" className="ms-2">{phoneValidationData.duplicates.length}</Badge>
                                </h5>
                                <p className="text-muted small">
                                    {language === 'es' 
                                        ? 'Los siguientes contactos duplicados serán omitidos del archivo final (se conserva la primera aparición):'
                                        : 'The following duplicate contacts will be omitted from the final file (first occurrence is kept):'}
                                </p>
                                
                                <div className="table-responsive" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                    <Table striped bordered size="sm" className="mb-0">
                                        <thead className="table-info sticky-top">
                                            <tr>
                                                <th className="text-nowrap small">{language === 'es' ? 'Fila Dup.' : 'Dup. Row'}</th>
                                                <th className="small d-none d-md-table-cell">{language === 'es' ? 'Contacto' : 'Contact'}</th>
                                                <th className="text-nowrap small">{language === 'es' ? 'Teléfono' : 'Phone'}</th>
                                                <th className="text-nowrap small">{language === 'es' ? '1ra Ap.' : '1st Occ.'}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {phoneValidationData.duplicates.map((duplicate, index) => (
                                                <tr key={index} className="table-warning">
                                                    <td>
                                                        <Badge bg="danger" className="small">{duplicate.rowIndex + 1}</Badge>
                                                    </td>
                                                    <td className="d-none d-md-table-cell">
                                                        <div className="text-truncate" style={{ maxWidth: '100px' }}>
                                                            <strong className="small">{duplicate.name}</strong>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <code className="text-muted small d-block text-truncate" style={{ maxWidth: '100px' }}>
                                                            {duplicate.phone}
                                                        </code>
                                                    </td>
                                                    <td>
                                                        <Badge bg="success" className="small">{duplicate.originalRowIndex + 1}</Badge>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </div>
                        )}
                        
                        <Alert variant="info" className="mb-0">
                            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                            <strong className="small">{language === 'es' ? '¿Qué sucederá?' : 'What will happen?'}</strong>
                            <ul className="mb-0 mt-2 small">
                                {phoneValidationData.issues.length > 0 && (
                                    <li>
                                        {language === 'es' 
                                            ? `${phoneValidationData.issues.length} números de teléfono serán corregidos automáticamente`
                                            : `${phoneValidationData.issues.length} phone numbers will be automatically corrected`}
                                    </li>
                                )}
                                {phoneValidationData.duplicates.length > 0 && (
                                    <li>
                                        {language === 'es' 
                                            ? `${phoneValidationData.duplicates.length} contactos duplicados serán removidos`
                                            : `${phoneValidationData.duplicates.length} duplicate contacts will be removed`}
                                    </li>
                                )}
                                <li>
                                    {language === 'es' 
                                        ? 'El procesamiento continuará normalmente con los datos corregidos'
                                        : 'Processing will continue normally with corrected data'}
                                </li>
                            </ul>
                        </Alert>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="p-2 p-md-3 d-flex flex-column flex-md-row gap-2">
                <Button 
                    variant="secondary" 
                    onClick={phoneValidationData?.onCancel}
                    className="w-100 w-md-auto"
                    size="sm"
                >
                    <FontAwesomeIcon icon={faTimes} className="me-2" />
                    {language === 'es' ? 'Cancelar' : 'Cancel'}
                </Button>
                <Button 
                    variant="success" 
                    onClick={phoneValidationData?.onConfirm}
                    className="w-100 w-md-auto"
                    size="sm"
                >
                    <FontAwesomeIcon icon={faCheck} className="me-2" />
                    {language === 'es' ? 'Continuar con Correcciones' : 'Continue with Corrections'}
                </Button>
            </Modal.Footer>
        </Modal>

        {/* Modal de Éxito */}
<Modal 
    show={showSuccessModal} 
    onHide={handleSuccessModalClose} 
    centered 
    size="lg"
    className="modal-mobile-fix"
    backdrop="static"
    style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh' }}
>
            <Modal.Header closeButton className="bg-success text-white">
                <Modal.Title className="fs-6 fs-md-5">
                    <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                    {language === 'es' ? '¡Procesamiento Exitoso!' : 'Processing Successful!'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-2 p-md-3" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {successModalData && (
                    <div>
                        <div className="text-center mb-3 mb-md-4">
                            <FontAwesomeIcon icon={faCheckCircle} size="2x" className="text-success mb-2 mb-md-3 d-none d-md-inline" />
                            <h4 className="text-success fs-6 fs-md-4">
                                {language === 'es' ? '¡Archivo procesado correctamente!' : 'File processed successfully!'}
                            </h4>
                        </div>
                        
                        <Row className="g-2 g-md-3">
                            <Col xs={12} md={6}>
                                <div className="d-flex flex-column gap-2 gap-md-3">
                                    <div className="border p-2 rounded bg-light">
                                        <strong className="small">{language === 'es' ? 'Archivo original:' : 'Original file:'}</strong>
                                        <br />
                                        <code className="small text-truncate d-block">{successModalData.fileName}</code>
                                    </div>
                                    <div className="border p-2 rounded bg-light">
                                        <strong className="small">{language === 'es' ? 'Contactos procesados:' : 'Contacts processed:'}</strong>
                                        <br />
                                        <Badge bg="success" className="fs-6">{successModalData.contactCount}</Badge>
                                    </div>
                                    {(successModalData.correctedPhones! > 0 || successModalData.duplicatesRemoved! > 0) && (
                                        <div className="border p-2 rounded bg-light">
                                            <strong className="small">{language === 'es' ? 'Correcciones aplicadas:' : 'Corrections applied:'}</strong>
                                            <br />
                                            <div className="d-flex flex-wrap gap-1 mt-1">
                                                {successModalData.correctedPhones! > 0 && (
                                                    <Badge bg="warning" className="small">
                                                        <FontAwesomeIcon icon={faPhone} className="me-1" />
                                                        {successModalData.correctedPhones} {language === 'es' ? 'tel.' : 'phones'}
                                                    </Badge>
                                                )}
                                                {successModalData.duplicatesRemoved! > 0 && (
                                                    <Badge bg="info" className="small">
                                                        <FontAwesomeIcon icon={faCopy} className="me-1" />
                                                        {successModalData.duplicatesRemoved} {language === 'es' ? 'dup.' : 'dup.'}
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </Col>
                            <Col xs={12} md={6}>
                                <div className="d-flex flex-column gap-2 gap-md-3">
                                    <div className="border p-2 rounded bg-light">
                                        <strong className="small">{language === 'es' ? 'Tiempo de procesamiento:' : 'Processing time:'}</strong>
                                        <br />
                                        <Badge bg="info" className="fs-6">{successModalData.processingTime}</Badge>
                                    </div>
                                    {successModalData.filePath && (
                                        <div className="border p-2 rounded bg-light">
                                            <strong className="small">{language === 'es' ? 'Guardado en servidor:' : 'Saved on server:'}</strong>
                                            <br />
                                            <small className="text-success">
                                                <FontAwesomeIcon icon={faCheck} className="me-1" />
                                                {language === 'es' ? 'Archivo respaldado' : 'File backed up'}
                                            </small>
                                        </div>
                                    )}
                                </div>
                            </Col>
                        </Row>
                        
                        <Alert variant="success" className="mt-3 mt-md-4 mb-0">
                            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                            {language === 'es' ? (
                                <>
                                    <strong className="small">¿Qué hacer ahora?</strong>
                                    <ul className="mb-0 mt-2 small">
                                        <li>El archivo CSV ha sido generado y {config.autoDownload ? 'descargado automáticamente' : 'está listo para descarga'}</li>
                                        <li>Los números de teléfono han sido validados y corregidos automáticamente</li>
                                        <li>Los contactos duplicados han sido removidos</li>
                                        <li>Puedes usar este archivo para campañas de WhatsApp o email marketing</li>
                                    </ul>
                                </>
                            ) : (
                                <>
                                    <strong className="small">What to do now?</strong>
                                    <ul className="mb-0 mt-2 small">
                                        <li>CSV file has been generated and {config.autoDownload ? 'automatically downloaded' : 'is ready for download'}</li>
                                        <li>Phone numbers have been validated and automatically corrected</li>
                                        <li>Duplicate contacts have been removed</li>
                                        <li>You can use this file for WhatsApp or email marketing campaigns</li>
                                    </ul>
                                </>
                            )}
                        </Alert>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="p-2 p-md-3 d-flex flex-column flex-md-row gap-2">
                {processedData && (
                    <Button 
                        variant="outline-primary" 
                        onClick={() => downloadCSV(processedData)}
                        className="w-100 w-md-auto order-md-first"
                        size="sm"
                    >
                        <FontAwesomeIcon icon={faDownload} className="me-2" />
                        {language === 'es' ? 'Descargar CSV' : 'Download CSV'}
                    </Button>
                )}
                <Button 
                    variant="success" 
                    onClick={handleSuccessModalClose} 
                    className="w-100 w-md-auto"
                    size="sm"
                >
                    <FontAwesomeIcon icon={faCheck} className="me-2" />
                    {language === 'es' ? 'Continuar' : 'Continue'}
                </Button>
            </Modal.Footer>
        </Modal>
   {/* Sistema de Toast Universal */}
{toast.show && toast.title && toast.message && (
    <div 
        className={`position-fixed top-0 start-50 translate-middle-x mt-3 toast-show`}
        style={{ 
            zIndex: 9998,
            transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            opacity: 1,
            transform: 'translateX(-50%) translateY(0)',
            pointerEvents: 'auto'
        }}
    >
        <Alert 
            variant={toast.variant || 'subtle-info'}
            className="shadow-lg border-0 d-flex align-items-center toast-alert"
            style={{
                minWidth: '320px',
                maxWidth: '500px'
            }}
        >
            <FontAwesomeIcon 
                icon={
                    toast.variant.includes('success') ? faCheckCircle :
                    toast.variant.includes('danger') ? faExclamationTriangle :
                    toast.variant.includes('warning') ? faExclamationCircle :
                    toast.variant.includes('info') ? faInfoCircle : faCheckCircle
                } 
                className={`me-2`}
                size="lg" 
            />
            <div className="flex-grow-1">
                <strong>{toast.title}</strong>
                {toast.message && (
                    <>
                        <br />
                        <small className="opacity-75">{toast.message}</small>
                    </>
                )}
            </div>
            <Button 
                variant={toast.variant.replace('subtle-', 'outline-') || 'outline-info'}
                size="sm"
                onClick={hideToast}
                className="ms-2"
                style={{ padding: '0.25rem 0.5rem' }}
            >
                <FontAwesomeIcon icon={faTimes} />
            </Button>
        </Alert>
    </div>
)}

        <Card className="mb-4">
            <Card.Header className="bg-primary text-white">
                <FontAwesomeIcon icon={faFileUpload} className="me-2" />
                {language === 'es' ? 'Procesador de Contactos v2.1' : 'Contact Processor v2.1'}
                <Badge bg="warning" className="ms-2">
                    <FontAwesomeIcon icon={faRobot} className="me-1" />
                    {language === 'es' ? 'Auto-corrección + Formato $' : 'Auto-correction + $ Format'}
                </Badge>
            </Card.Header>
            <Card.Body>
                <Tabs 
                    defaultActiveKey="upload" 
                    activeKey={activeTab} 
                    onSelect={(eventKey) => setActiveTab(eventKey || 'upload')} 
                    className="mb-3"
                >
                    {/* ================ PESTAÑA DE SUBIDA Y PROCESAMIENTO ================ */}
                    <Tab 
                        eventKey="upload" 
                        title={
                            <span>
                                <FontAwesomeIcon icon={faFileUpload} className="me-2" />{language === 'es' ? 'Procesar Archivo' : 'Process File'} 
                            </span>
                        }
                        >
                        {error && (
                            <Alert variant="danger" dismissible onClose={() => setError(null)}>
                                <div style={{ whiteSpace: 'pre-line' }}>
                                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                                    {error}
                                </div>
                            </Alert>
                        )}
                        
                        {success && (
                            <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
                                <FontAwesomeIcon icon={faCheck} className="me-2" />
                                {success}
                            </Alert>
                        )}

                        {validationResult && validationResult.warnings.length > 0 && (
                            <Alert key="warning" variant="subtle-warning" className="mt-3 mb-0 text-body-emphasis">
                                <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                                <strong>{language === 'es' ? 'Advertencias detectadas:' : 'Warnings detected:'}</strong>
                                <ul className="mb-0 mt-2">
                                    {validationResult.warnings.map((warning, index) => (
                                        <li key={index}>{warning}</li>
                                    ))}
                                </ul>
                                
                                {(validationResult.phoneIssues.length > 0 || validationResult.duplicates.length > 0) && (
                                    <div className="mt-3">
                                        <small className="text-info">
                                            <FontAwesomeIcon icon={faRobot} className="me-1" />
                                            {language === 'es' 
                                                ? 'El sistema aplicará correcciones automáticas durante el procesamiento.'
                                                : 'The system will apply automatic corrections during processing.'}
                                        </small>
                                    </div>
                                )}
                            </Alert>
                        )}
                        
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label>{language === 'es' ? 'Subir archivo de contactos' : 'Upload contact file'}</Form.Label>
                                <Form.Control
                                    type="file"
                                    accept=".xlsx,.xls,.csv"
                                    onChange={handleFileChange}
                                    disabled={disabled || isProcessing}
                                />
                                <Form.Text className="text-muted">
                                    {language === 'es'
                                        ? `Formatos soportados: Excel (.xlsx, .xls), CSV con estructura definida. Los números de teléfono serán validados automáticamente.`
                                        : 'Supported formats: Excel (.xlsx, .xls), CSV with defined structure. Phone numbers will be automatically validated.'}
                                </Form.Text>
                            </Form.Group>

                            {/* Información de formato requerido con mejoras */}
                            <Accordion className="mb-4">
    <Accordion.Item eventKey="0">
        <Accordion.Header>
            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
            <strong>{language === 'es' ? 'Formato requerido y validaciones automáticas' : 'Required format and automatic validations'}</strong>
        </Accordion.Header>
        <Accordion.Body className="pt-2 bg-body-emphasis">
                                <br />
                                {language === 'es' ? (
                                    <>
                                    <Alert key="info" variant="subtle-info" className="mt-3 mb-0 text-body-emphasis">
                                        <strong>Estructura del archivo:</strong><br />
                                        • La columna 4 debe contener: <code>CL,Nombre,email,telefono,curso</code><br />
                                        • Ejemplo: <code>"CL,Camila,camila.to@gmail.com,+56933910115,Selectividad alimentaria..."</code><br />
                                        • Cada fila debe tener al menos 4 columnas<br /><br />
                                        
                                        <strong>🤖 Correcciones automáticas de teléfonos:</strong><br />
                                        • Se aceptan números internacionales válidos (+1, +34, +44, etc.)<br />
                                        • Números sin código de país se asumen chilenos (+56)<br />
                                        • Formato requerido: +[código_país][número] (7-15 dígitos)<br />
                                        • Caracteres especiales se eliminan automáticamente<br /><br />
                                        
                                        <strong>🔍 Detección de duplicados:</strong><br />
                                        • Se identificarán números de teléfono duplicados<br />
                                        • Se conservará solo la primera aparición<br />
                                        • Se mostrará un reporte de elementos removidos<br /><br />
                                        
                                        <strong>💰 Formato monetario automático:</strong><br />
                                        • Variables con "precio", "costo", "total", "valor" se formatean automáticamente<br />
                                        • Ejemplo: [precio] con valor "150000" se muestra como "$150.000"<br />
                                        • Compatible con formatos chilenos e internacionales
                                    </Alert>
                                    </>
                                ) : (
                                    <>
                                    <Alert key="info" variant="subtle-info" className="mt-3 mb-0 text-body-emphasis">
                                        <strong>File structure:</strong><br />
                                        • Column 4 must contain: <code>CL,Name,email,phone,course</code><br />
                                        • Example: <code>"CL,Camila,camila.to@gmail.com,+56933910115,Food selectivity..."</code><br />
                                        • Each row must have at least 4 columns<br /><br />
                                        
                                        <strong>🤖 Automatic phone corrections:</strong><br />
                                        • International numbers are accepted (+1, +34, +44, etc.)<br />
                                        • Numbers without country code are assumed Chilean (+56)<br />
                                        • Required format: +[country_code][number] (7-15 digits)<br />
                                        • Special characters are automatically removed<br /><br />
                                        
                                        <strong>🔍 Duplicate detection:</strong><br />
                                        • Duplicate phone numbers will be identified<br />
                                        • Only the first occurrence will be kept<br />
                                        • A report of removed items will be shown<br /><br />
                                        
                                        <strong>💰 Automatic monetary formatting:</strong><br />
                                        • Variables with "precio", "costo", "total", "valor" are automatically formatted<br />
                                        • Example: [precio] with value "150000" displays as "$150.000"<br />
                                        • Compatible with Chilean and international formats
                                    </Alert>
                                    </>
                                )}
                            </Accordion.Body>
    </Accordion.Item>
</Accordion>
                            
                            {uploadedFile && validationResult && validationResult.isValid && previewData.length > 0 && (
                                <>
                                    <Alert key="success" variant="subtle-success" className="mt-3 mb-0 text-body-emphasis">
                                        <FontAwesomeIcon icon={faCheck} className="me-2" />
                                        {language === 'es'
                                            ? `✅ Archivo validado: ${uploadedFile.name} - ${contactCount} contactos válidos encontrados`
                                            : `✅ File validated: ${uploadedFile.name} - ${contactCount} valid contacts found`}
                                        
                                        {(validationResult.phoneIssues.length > 0 || validationResult.duplicates.length > 0) && (
                                            <div className="mt-2">
                                                <small className="text-info">
                                                    <FontAwesomeIcon icon={faRobot} className="me-1" />
                                                    {language === 'es' 
                                                        ? `Se aplicarán ${validationResult.phoneIssues.length + validationResult.duplicates.length} correcciones automáticas`
                                                        : `${validationResult.phoneIssues.length + validationResult.duplicates.length} automatic corrections will be applied`}
                                                </small>
                                            </div>
                                        )}
                                    </Alert>
                                    
                                    <h5>{language === 'es' ? 'Vista previa de contactos extraídos' : 'Preview of extracted contacts'}</h5>
                                    <div className="table-responsive mb-4">
                                        <table className="table table-sm table-bordered">
                                            <thead>
                                                <tr>
                                                    <th>{language === 'es' ? 'Fila' : 'Row'}</th>
                                                    <th>{language === 'es' ? 'Nombre' : 'Name'}</th>
                                                    <th>{language === 'es' ? 'Teléfono' : 'Phone'}</th>
                                                    <th>{language === 'es' ? 'Curso' : 'Course'}</th>
                                                    <th>{language === 'es' ? 'Estado' : 'Status'}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {previewData.map((contact, idx) => (
                                                    <tr key={idx} className={contact.necesita_correccion ? 'table-warning' : ''}>
                                                        <td><Badge bg="secondary">{contact.fila}</Badge></td>
                                                        <td><strong>{contact.nombre}</strong></td>
                                                        <td>
                                                            {contact.necesita_correccion ? (
                                                                <div>
                                                                    <code className="text-danger">{contact.telefono}</code>
                                                                    <FontAwesomeIcon icon={faSync} className="text-warning mx-2" />
                                                                    <code className="text-success">{contact.telefono_corregido}</code>
                                                                </div>
                                                            ) : (
                                                                <code className="text-success">{contact.telefono}</code>
                                                            )}
                                                        </td>
                                                        <td className="text-truncate" style={{maxWidth: '300px'}} title={contact.curso}>
                                                            {contact.curso}
                                                        </td>
                                                        <td>
                                                            {contact.necesita_correccion ? (
                                                                <Badge bg="warning">
                                                                    <FontAwesomeIcon icon={faExclamationCircle} className="me-1" />
                                                                    {language === 'es' ? 'Corregir' : 'Fix'}
                                                                </Badge>
                                                            ) : (
                                                                <Badge bg="success">
                                                                    <FontAwesomeIcon icon={faCheck} className="me-1" />
                                                                    {language === 'es' ? 'OK' : 'OK'}
                                                                </Badge>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {contactCount > 10 && (
                                            <small className="text-body-tertiary">
                                                {language === 'es' 
                                                    ? `Mostrando primeros 10 de ${contactCount} contactos totales`
                                                    : `Showing first 10 of ${contactCount} total contacts`}
                                            </small>
                                        )}
                                    </div>
                                </>
                            )}

                            {/* Configuración de variables personalizadas */}
<Accordion className="mb-4">
    <Accordion.Item eventKey="0">
        <Accordion.Header>
            <FontAwesomeIcon icon={faCog} className="me-2" />
            <strong>{language === 'es' ? 'Variables personalizadas' : 'Custom variables'}</strong>
            <Badge bg="warning" className="ms-2" style={{ fontSize: '0.7rem' }}>
                💰 Auto-formato
            </Badge>
        </Accordion.Header>
        <Accordion.Body className="pt-2">
                            <Alert key="success" variant="subtle-success" className="mt-3 mb-0 text-body-emphasis">
                                <p className="text-muted">
                                    {language === 'es'
                                        ? 'Define variables que podrás usar en tu mensaje. Las variables monetarias (precio, costo, total, valor) se formatean automáticamente.'
                                        : 'Define variables you can use in your message. Monetary variables (precio, costo, total, valor) are automatically formatted.'}
                                </p>
                                
                                {variables.map((variable, index) => {
                                    const isMonetary = isMonetaryVariable(variable.name);
                                    const displayValue = isMonetary && variable.value 
                                        ? formatMonetaryValue(variable.value) 
                                        : variable.value;
                                    
                                    return (
                                        <Row key={variable.id} className="mb-3 align-items-start">
                                            <Col md={4}>
                                                <Form.Control
                                                    type="text"
                                                    placeholder={language === 'es' ? 'Nombre variable' : 'Variable name'}
                                                    value={variable.name}
                                                    onChange={(e) => updateVariable(variable.id, 'name', e.target.value)}
                                                    disabled={disabled || isProcessing}
                                                    className={isMonetary ? 'border-warning' : ''}
                                                />
                                                {isMonetary && (
                                                    <small className="text-warning mt-1 d-block">
                                                        <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                                                        💰 {language === 'es' ? 'Variable monetaria' : 'Monetary variable'}
                                                    </small>
                                                )}
                                            </Col>
                                            <Col md={6}>
                                                <Form.Control
                                                    type="text"
                                                    placeholder={language === 'es' ? 'Valor de la variable' : 'Variable value'}
                                                    value={variable.value}
                                                    onChange={(e) => updateVariable(variable.id, 'value', e.target.value)}
                                                    disabled={disabled || isProcessing}
                                                    className={isMonetary ? 'border-warning' : ''}
                                                />
                                                {isMonetary && variable.value && (
                                                    <small className="text-warning mt-1 d-block">
                                                        <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                                                        {language === 'es' ? 'Se mostrará como:' : 'Will display as:'} <strong>{displayValue}</strong>
                                                    </small>
                                                )}
                                            </Col>
                                            <Col md={2}>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => removeVariable(variable.id)}
                                                    disabled={disabled || isProcessing}
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </Button>
                                            </Col>
                                        </Row>
                                    );
                                })}
                                
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={addVariable}
                                    disabled={disabled || isProcessing}
                                    className="mt-2"
                                >
                                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                                    {language === 'es' ? 'Agregar variable' : 'Add variable'}
                                </Button>
                                
                                <div className="mt-3">
                                    <small className="text-info">
                                        <strong>{language === 'es' ? 'Variables disponibles:' : 'Available variables:'}</strong>
                                        <br />
                                        <code>[nombre]</code> - {language === 'es' ? 'Nombre del contacto' : 'Contact name'}
                                        <br />
                                        <code>[curso]</code> - {language === 'es' ? 'Curso consultado' : 'Consulted course'}
                                        <br />
                                        {variables.filter(v => v.name).map(v => {
                                            const isMonetary = isMonetaryVariable(v.name);
                                            const displayValue = isMonetary && v.value 
                                                ? formatMonetaryValue(v.value) 
                                                : v.value;
                                            return (
                                                <span key={v.id}>
                                                    <code>[{v.name}]</code> - {displayValue}
                                                    {isMonetary && (
                                                        <Badge bg="warning" className="ms-1" style={{ fontSize: '0.6rem' }}>
                                                            💰 Auto-formato
                                                        </Badge>
                                                    )}
                                                    <br />
                                                </span>
                                            );
                                        })}
                                    </small>
                                    
                                    {variables.some(v => isMonetaryVariable(v.name)) && (
                                        <Alert key="info" variant="subtle-info" className="mt-3 mb-0 text-body-emphasis">
                                            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                                            <small>
                                                <strong>💰 {language === 'es' ? 'Formato monetario automático:' : 'Automatic monetary formatting:'}</strong>
                                                <br />
                                                {language === 'es' 
                                                    ? 'Variables con nombres como "precio", "costo", "total", "valor" se formatean automáticamente a pesos chilenos (ej: 150000 → $150.000)'
                                                    : 'Variables with names like "precio", "costo", "total", "valor" are automatically formatted to Chilean pesos (e.g: 150000 → $150.000)'}
                                            </small>
                                        </Alert>
                                    )}
                                </div>
                            </Alert>
                                    </Accordion.Body>
    </Accordion.Item>
</Accordion>

                            {/* Editor de mensaje personalizado */}
                            <div className="mb-4">
<Form.Group>
<Form.Label>
    <FontAwesomeIcon icon={faPen} className="me-2" />
    {language === 'es' ? 'Mensaje personalizado' : 'Custom message'}
    <span className="text-danger">*</span>
    {/* CONTADORES ACTUALIZADOS */}
    <span className="ms-2">
        <Badge bg="secondary" className="me-1">
            {customMessage.length} chars
        </Badge>
<Badge bg={
    (() => {
        const { finalLength } = calculateFinalMessageLength();
        if (finalLength <= 200) return 'success';           // Verde: Óptimo (100 pts)
        if (finalLength <= 300) return 'warning';           // Amarillo: Medio (50 pts)
        if (finalLength <= 450) return 'warning';           // Amarillo: Riesgoso (10-50 pts)
        if (finalLength <= 600) return 'danger';            // Rojo: Crítico (0-10 pts)
        return 'danger';                                     // Rojo: Crítico absoluto (0 pts)
    })()
} className="me-1">
    Final: {(() => {
        const { finalLength } = calculateFinalMessageLength();
        return finalLength;
    })()} chars
</Badge>
        <Badge bg={
            (() => {
                const { linkCount } = calculateFinalMessageLength();
                if (linkCount === 0) return 'success';
                if (linkCount <= 2) return 'warning';
                return 'danger';
            })()
        }>
            {(() => {
                const { linkCount } = calculateFinalMessageLength();
                return linkCount === 0 ? 'Sin enlaces' : `${linkCount} enlace${linkCount > 1 ? 's' : ''}`;
            })()}
        </Badge>
    </span>
</Form.Label>
    <Form.Control
        as="textarea"
        rows={8}
        placeholder={language === 'es' 
            ? 'Escribe tu mensaje aquí. Puedes usar variables como [nombre], [curso], [precio], etc.' 
            : 'Write your message here. You can use variables like [nombre], [curso], [precio], etc.'}
        value={customMessage}
        onChange={(e) => {
            setCustomMessage(e.target.value);
            if (e.target.value.toLowerCase().includes('toasty')) {
                triggerToastyEasterEgg();
            }
        }}
        disabled={disabled || isProcessing}
        style={{ fontFamily: 'monospace' }}
    />
<Form.Text className="text-muted">
    {(() => {
        const { finalLength, linkCount, emojiCountFinal } = calculateFinalMessageLength();
        
        // Calcular score de longitud para mostrar
        let lengthScore = 100;
        if (finalLength < 20) {
            lengthScore = 30;
        } else if (finalLength <= 200) {
            lengthScore = 100;
        } else if (finalLength <= 300) {
            const excess = finalLength - 200;
            lengthScore = Math.max(50, 100 - (excess / 100) * 50);
        } else if (finalLength <= 450) {
            const excess = finalLength - 300;
            lengthScore = Math.max(10, 50 - (excess / 150) * 40);
        } else if (finalLength <= 600) {
            const excess = finalLength - 450;
            lengthScore = Math.max(0, 10 - (excess / 150) * 10);
        } else {
            lengthScore = 0;
        }
        lengthScore = Math.round(lengthScore);
        
        // Alertas por prioridad
        if (linkCount >= 3) {
            return language === 'es'
                ? `🚨 CRÍTICO: ${linkCount} enlaces = riesgo máximo, reducir urgentemente`
                : `🚨 CRITICAL: ${linkCount} links = maximum risk, reduce urgently`;
        }
        if (finalLength >= 600) {
            return language === 'es'
                ? `💀 CRÍTICO: Mensaje muy largo (${finalLength} chars, 0 pts), reducir a menos de 200`
                : `💀 CRITICAL: Message too long (${finalLength} chars, 0 pts), reduce to under 200`;
        }
        if (emojiCountFinal > 3) {
            return language === 'es'
                ? `🚨 PELIGRO: Demasiados emojis (${emojiCountFinal}), máximo 2 recomendado`
                : `🚨 DANGER: Too many emojis (${emojiCountFinal}), maximum 2 recommended`;
        }
        if (finalLength > 450) {
            return language === 'es'
                ? `🚨 ZONA CRÍTICA: ${finalLength} chars (${lengthScore} pts), reducir urgentemente`
                : `🚨 CRITICAL ZONE: ${finalLength} chars (${lengthScore} pts), reduce urgently`;
        }
        if (finalLength > 300) {
            return language === 'es'
                ? `⚠️ ZONA RIESGOSA: ${finalLength} chars (${lengthScore} pts), considerar reducir`
                : `⚠️ RISKY ZONE: ${finalLength} chars (${lengthScore} pts), consider reducing`;
        }
        if (finalLength > 200) {
            return language === 'es'
                ? `ℹ️ ZONA MEDIA: ${finalLength} chars (${lengthScore} pts), aún aceptable pero mejorable`
                : `ℹ️ MID ZONE: ${finalLength} chars (${lengthScore} pts), acceptable but improvable`;
        }
        if (linkCount === 2) {
            return language === 'es'
                ? `⚠️ ALTO RIESGO: 2 enlaces aumentan riesgo 50%, considerar reducir a 1`
                : `⚠️ HIGH RISK: 2 links increase risk 50%, consider reducing to 1`;
        }
        if (linkCount === 1) {
            return language === 'es'
                ? `⚠️ 1 enlace aumenta riesgo 25%, usar con precaución`
                : `⚠️ 1 link increases risk 25%, use with caution`;
        }
        if (finalLength < 20) {
            return language === 'es'
                ? `⚠️ Mensaje muy corto (${lengthScore} pts), puede verse sospechoso`
                : `⚠️ Message too short (${lengthScore} pts), may look suspicious`;
        }
        
        return language === 'es' 
            ? `✅ ZONA ÓPTIMA: ${finalLength} chars (${lengthScore} pts) - excelente para evitar flags`
            : `✅ OPTIMAL ZONE: ${finalLength} chars (${lengthScore} pts) - excellent to avoid flags`;
    })()}
</Form.Text>
</Form.Group>
                            </div>
                            
                            {isProcessing && (
                                <div className="mb-4">
                                    <h5>{language === 'es' ? 'Procesando contactos...' : 'Processing contacts...'}</h5>
                                    <ProgressBar now={processingProgress} label={`${processingProgress}%`} animated />
                                    {processingStats && (
                                        <small className="text-body-tertiary">
                                            {language === 'es' 
                                                ? `Procesando ${processingStats.totalRows} filas, corrigiendo teléfonos, formateando variables monetarias y removiendo duplicados...`
                                                : `Processing ${processingStats.totalRows} rows, correcting phones, formatting monetary variables and removing duplicates...`}
                                        </small>
                                    )}
                                </div>
                            )}
                            
                            <Row sm={4} lg={4} xl={4}>
                                <Col>
                            <Button 
                                variant="outline-success" 
                                onClick={processFile}
                                disabled={disabled || isProcessing || !uploadedFile || !customMessage.trim() || !validationResult?.isValid || (whatsAppStatus ? !whatsAppStatus.ready : true)}
                                className="mt-3"
                                size="lg"
                            >
                                {isProcessing && whatsAppStatus && !whatsAppStatus.ready ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        {language === 'es' ? 'Procesando...' : 'Processing...'}
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faRobot} className="me-2" />
                                        {language === 'es' ? 'Procesar Envío' : 'Process Sending'}
                                    </>
                                )}
                            </Button>
</Col>
<Col>
                            <Button 
            variant="outline-secondary" 
            onClick={cancelProcessing}
            disabled={isCancelling}
            size="lg"
            className="mt-3"
        >
            {isCancelling ? (
                <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {language === 'es' ? 'Cancelando...' : 'Cancelling...'}
                </>
            ) : (
                <>
                    <FontAwesomeIcon icon={faTimes} className="me-2" />
                    {language === 'es' ? 'Cancelar' : 'Cancel'}
                </>
            )}
        </Button>
        </Col>
                            </Row>
                            {uploadedFile && (!validationResult?.isValid) && (
                                <Alert variant="warning" className="mt-3">
                                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                                    {language === 'es'
                                        ? 'El archivo debe pasar la validación antes de poder procesarlo'
                                        : 'File must pass validation before it can be processed'}
                                </Alert>
                            )}
                        </Form>
                    </Tab>
                    
                    {/* ================ ✨ PESTAÑA DE CONFIGURACIÓN ACTUALIZADA ================ */}
                    <Tab 
                        eventKey="config" 
                        title={
                            <span>
                                <FontAwesomeIcon icon={faCog} className="me-2" />
                                {language === 'es' ? 'Configuración' : 'Configuration'}
                                {whatsAppStatus && !whatsAppStatus.ready && (
                                    <Badge bg="warning" className="ms-1">!</Badge>
                                )}
                            </span>
                        }
                    >
                        {error && (
                            <Alert variant="danger" dismissible onClose={() => setError(null)}>
                                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                                {error}
                            </Alert>
                        )}
                        
                        {success && (
                            <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
                                <FontAwesomeIcon icon={faCheck} className="me-2" />
                                {success}
                            </Alert>
                        )}
                        
                        {isLoading || isLoadingWhatsApp ? (
                            <div className="text-center py-4">
                                <Spinner animation="border" />
                                <p className="mt-2">{language === 'es' ? 'Cargando configuración...' : 'Loading configuration...'}</p>
                            </div>
                        ) : (
                            <>
                                {/* ✨ ESTADO DE WHATSAPP */}
                                <div className="mb-4">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5>
                                            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                                            {language === 'es' ? 'Estado de WhatsApp' : 'WhatsApp Status'}
                                        </h5>
                                        <Button 
                                            variant="outline-secondary" 
                                            size="sm"
                                            onClick={loadWhatsAppStatus}
                                            disabled={isLoadingWhatsApp}
                                        >
                                            <FontAwesomeIcon icon={faSync} className={isLoadingWhatsApp ? "fa-spin me-2" : "me-2"} />
                                            {language === 'es' ? 'Actualizar' : 'Refresh'}
                                        </Button>
                                    </div>

                                    {whatsAppStatus ? (
                                        <Alert key={whatsAppStatus.ready ? 'success' : 'warning'} variant={whatsAppStatus.ready ? 'subtle-success' : 'subtle-warning'}>
                                            <div className="d-flex align-items-center mb-2">
                                                <FontAwesomeIcon 
                                                    icon={whatsAppStatus.ready ? faCheckCircle : faExclamationTriangle} 
                                                    className="me-2" 
                                                />
                                                <strong>
                                                    {whatsAppStatus.ready 
                                                        ? (language === 'es' ? 'Configuración completa' : 'Configuration complete')
                                                        : (language === 'es' ? 'Configuración requerida' : 'Configuration required')
                                                    }
                                                </strong>
                                            </div>
                                            
                                            <Row className="g-2">
                                                <Col md={4}>
                                                    <div className="d-flex align-items-center">
                                                        <Badge bg={whatsAppStatus.envExists ? 'success' : 'danger'} className="me-2">
                                                            <FontAwesomeIcon icon={whatsAppStatus.envExists ? faCheck : faTimes} />
                                                        </Badge>
                                                        <small>
                                                            {language === 'es' ? 'Archivo .env' : '.env file'}
                                                        </small>
                                                    </div>
                                                </Col>
                                                <Col md={4}>
                                                    <div className="d-flex align-items-center">
                                                        <Badge bg={whatsAppStatus.envValid ? 'success' : 'warning'} className="me-2">
                                                            <FontAwesomeIcon icon={whatsAppStatus.envValid ? faCheck : faExclamationTriangle} />
                                                        </Badge>
                                                        <small>
                                                            {language === 'es' ? 'Variables válidas' : 'Valid variables'}
                                                        </small>
                                                    </div>
                                                </Col>
                                                <Col md={4}>
                                                    <div className="d-flex align-items-center">
                                                        <Badge bg={whatsAppStatus.scheduleConfigured ? 'success' : 'info'} className="me-2">
                                                            <FontAwesomeIcon icon={whatsAppStatus.scheduleConfigured ? faCheck : faClock} />
                                                        </Badge>
                                                        <small>
                                                            {language === 'es' ? 'Horarios configurados' : 'Schedule configured'}
                                                        </small>
                                                    </div>
                                                </Col>
                                            </Row>
                                            
                                            {whatsAppStatus.schedule && whatsAppStatus.scheduleConfigured && (
                                                <div className="mt-3 p-2 bg-light rounded">
                                                    <small className="text-body-tertiary">
                                                        <FontAwesomeIcon icon={faClock} className="me-1" />
                                                        <strong>{language === 'es' ? 'Horario actual:' : 'Current schedule:'}</strong>
                                                        <br />
                                                        {whatsAppStatus.schedule.summary || 
                                                         `${whatsAppStatus.schedule.timeIntervalInit || '09:00'} - ${whatsAppStatus.schedule.timeIntervalEnd || '18:00'}, ${formatDaysForDisplay((whatsAppStatus.schedule.daysInterval || []).join(','))}`
                                                        }
                                                    </small>
                                                </div>
                                            )}
                                        </Alert>
                                    ) : (
                                        <Alert variant="info">
                                            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                                            {language === 'es' 
                                                ? 'Cargando estado de WhatsApp...' 
                                                : 'Loading WhatsApp status...'}
                                        </Alert>
                                    )}
                                </div>

                                {/* ✨ CONFIGURACIÓN DE HORARIOS */}
                                <div className="mb-4 border p-3 rounded bg-body-emphasis">

                                    <h5>
                                        <FontAwesomeIcon icon={faClock} className="me-2" />
                                        {language === 'es' ? 'Configuración de Horarios' : 'Schedule Configuration'}
                                        <Badge bg="info" className="ms-2" style={{ fontSize: '0.7rem' }}>
                                            WhatsApp
                                        </Badge>
                                    </h5>
                                    <p className="text-muted">
                                        {language === 'es'
                                            ? 'Configura los horarios y días permitidos para el envío de mensajes de WhatsApp.'
                                            : 'Configure the allowed times and days for sending WhatsApp messages.'}
                                    </p>
                                    
                                    <Row className="g-3">
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>
                                                    <FontAwesomeIcon icon={faClock} className="me-2" />
                                                    {language === 'es' ? 'Hora de inicio' : 'Start time'}
                                                </Form.Label>
                                                <Form.Control
                                                    type="time"
                                                    value={config.timeIntervalInit || '09:00'}
                                                    onChange={(e) => setConfig({...config, timeIntervalInit: e.target.value})}
                                                    disabled={disabled || isSavingSchedule}
                                                />
                                                <Form.Text className="text-muted">
                                                    {language === 'es'
                                                        ? 'Hora más temprana para enviar mensajes'
                                                        : 'Earliest time to send messages'}
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                        
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>
                                                    <FontAwesomeIcon icon={faClock} className="me-2" />
                                                    {language === 'es' ? 'Hora de fin' : 'End time'}
                                                </Form.Label>
                                                <Form.Control
                                                    type="time"
                                                    value={config.timeIntervalEnd || '18:00'}
                                                    onChange={(e) => setConfig({...config, timeIntervalEnd: e.target.value})}
                                                    disabled={disabled || isSavingSchedule}
                                                />
                                                <Form.Text className="text-muted">
                                                    {language === 'es'
                                                        ? 'Hora más tardía para enviar mensajes'
                                                        : 'Latest time to send messages'}
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                        
                                        <Col xs={12}>
                                            <Form.Group>
                                                <Form.Label>
                                                    <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                                                    {language === 'es' ? 'Días permitidos' : 'Allowed days'}
                                                </Form.Label>
                                                <div className="d-flex flex-wrap gap-2 mt-2">
{getDayOptions().map(day => {
    const selectedDays = (config.daysInterval || 'MON,TUE,WED,THU,FRI').split(',');
    const isSelected = selectedDays.includes(day.value);
    const isWeekend = ['SAT', 'SUN'].includes(day.value);
    
    return (
        <Form.Check
            key={day.value}
            type="checkbox"
            id={`day-${day.value}`}
            label={
                <span className={isWeekend ? 'text-warning' : ''}>
                    {day.label}
                    {isWeekend && ' ⚠️'}
                </span>
            }
            checked={isSelected}
            onChange={(e) => {
                let newDays = selectedDays.filter(d => d !== day.value);
                if (e.target.checked) {
                    newDays.push(day.value);
                }
                // Mantener el orden original
                const orderedDays = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']
                    .filter(d => newDays.includes(d));
                setConfig({...config, daysInterval: orderedDays.join(',')});
            }}
            disabled={disabled || isSavingSchedule}
            className={`me-3 ${isWeekend ? 'border-warning' : ''}`}
        />
    );
})}
                                                </div>
                                                <Form.Text className="text-muted">
                                                    {language === 'es'
                                                        ? 'Selecciona los días de la semana permitidos para enviar mensajes'
                                                        : 'Select the days of the week allowed for sending messages'}
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    
                                    {/* Preview del horario */}
                                    <Alert key="info" variant="subtle-info" className="mt-3 mb-0 text-body-emphasis">

                                        <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                                        <strong>{language === 'es' ? 'Resumen:' : 'Summary:'}</strong>
                                        <br />
                                        {language === 'es'
                                            ? `Los mensajes se enviarán ${formatDaysForDisplay(config.daysInterval || 'MON,TUE,WED,THU,FRI')} de ${config.timeIntervalInit || '09:00'} a ${config.timeIntervalEnd || '18:00'}`
                                            : `Messages will be sent ${formatDaysForDisplay(config.daysInterval || 'MON,TUE,WED,THU,FRI')} from ${config.timeIntervalInit || '09:00'} to ${config.timeIntervalEnd || '18:00'}`}
                                    </Alert>
                                </div>

                                {/* Configuración general del procesador */}
                                <div className="mb-4">
                                    <h5>
                                        <FontAwesomeIcon icon={faCog} className="me-2" />
                                        {language === 'es' ? 'Configuración General' : 'General Configuration'}
                                    </h5>
                                    
                                    <Form.Group className="mb-3">
                                        <Form.Check 
                                            type="switch"
                                            id="processor-enabled"
                                            label={language === 'es' ? 'Activar procesador de archivos' : 'Enable file processor'}
                                            checked={config.enabled}
                                            onChange={(e) => setConfig({...config, enabled: e.target.checked})}
                                            disabled={disabled}
                                        />
                                    </Form.Group>
                                    
                                    <Form.Group className="mb-3">
                                        <Form.Check 
                                            type="switch"
                                            id="auto-download-enabled"
                                            label={language === 'es' ? 'Descarga automática de archivos procesados' : 'Auto-download processed files'}
                                            checked={config.autoDownload}
                                            onChange={(e) => setConfig({...config, autoDownload: e.target.checked})}
                                            disabled={disabled || !config.enabled}
                                        />
                                        <Form.Text className="text-muted">
                                            {language === 'es'
                                                ? 'Descarga automáticamente el archivo CSV después del procesamiento'
                                                : 'Automatically downloads CSV file after processing'}
                                        </Form.Text>
                                    </Form.Group>
                                    {/* ✨ NUEVA SECCIÓN: PRIMERA RESPUESTA RANDOM */}
<div className="mb-4 border p-3 rounded bg-body-emphasis">

    <h5>
        <FontAwesomeIcon icon={faRobot} className="me-2" />
        {language === 'es' ? 'Primera Respuesta Random' : 'Random First Response'}
        <Badge bg="primary" className="ms-2" style={{ fontSize: '0.7rem' }}>
            🤖 IA
        </Badge>
        {firstResponseConfig.enabled && (
            <Badge bg="success" className="ms-2" style={{ fontSize: '0.7rem' }}>
                ACTIVO
            </Badge>
        )}
    </h5>
    <p className="text-body-tertiary">

        {language === 'es'
            ? 'Activa respuestas automáticas inteligentes para nuevos contactos, independientemente del estado del bot o copilot.'
            : 'Activate intelligent automatic responses for new contacts, regardless of bot or copilot status.'}
    </p>
    
    {isLoadingFirstResponse ? (
        <div className="text-center py-3">
            <Spinner animation="border" size="sm" />
            <small className="ms-2 text-muted">
                {language === 'es' ? 'Cargando configuración...' : 'Loading configuration...'}
            </small>
        </div>
    ) : (
        <>
            <Form.Group className="mb-3">
                <Form.Check 
                    type="switch"
                    id="first-response-enabled"
                    label={
                        <span>
                            {language === 'es' ? 'Activar Primera Respuesta Random' : 'Enable Random First Response'}
                            <Badge bg="info" className="ms-2" style={{ fontSize: '0.6rem' }}>
                                {language === 'es' ? 'Independiente del bot' : 'Independent of bot'}
                            </Badge>
                        </span>
                    }
                    checked={firstResponseConfig.enabled}
                    onChange={(e) => setFirstResponseConfig({
                        ...firstResponseConfig, 
                        enabled: e.target.checked
                    })}
                    disabled={disabled || isSavingFirstResponse}
                />
                <Form.Text className="text-muted">
                    {language === 'es'
                        ? 'Envía automáticamente una respuesta inteligente a nuevos contactos usando IA'
                        : 'Automatically sends an intelligent response to new contacts using AI'}
                </Form.Text>
            </Form.Group>
            
            {firstResponseConfig.enabled && (
                <Row className="g-3">
                    <Col md={8}>
                        <Form.Group>
                            <Form.Label>
                                <FontAwesomeIcon icon={faCog} className="me-2" />
                                {language === 'es' ? 'Contexto de respuestas' : 'Response context'}
                            </Form.Label>
                            <Form.Select
                                value={firstResponseConfig.context}
                                onChange={(e) => setFirstResponseConfig({
                                    ...firstResponseConfig,
                                    context: e.target.value
                                })}
                                disabled={disabled || isSavingFirstResponse}
                            >
                                {firstResponseContexts.length > 0 ? (
                                    firstResponseContexts.map(context => (
                                        <option key={context.key} value={context.key}>
                                            {context.name} - {context.description}
                                        </option>
                                    ))
                                ) : (
                                    <>
                                        <option value="marketing_general">Marketing General</option>
                                        <option value="inmobiliario">Inmobiliario</option>
                                        <option value="educativo">Educativo</option>
                                        <option value="ventas_consultoria">Ventas y Consultoría</option>
                                    </>
                                )}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    
                    <Col md={4}>
                        <Form.Label className="d-block">
                            {language === 'es' ? 'Prueba rápida' : 'Quick test'}
                        </Form.Label>
                        <Button 
                            variant="outline-primary" 
                            size="sm"
                            onClick={testFirstResponse}
                            disabled={disabled || isSavingFirstResponse || !firstResponseConfig.enabled}
                            className="w-100"
                        >
                            <FontAwesomeIcon icon={faRobot} className="me-2" />
                            {language === 'es' ? 'Probar IA' : 'Test AI'}
                        </Button>
                    </Col>
                </Row>
            )}
            
            {/* Estadísticas */}
            {firstResponseStats && firstResponseConfig.enabled && (
                <Alert key="info" variant="subtle-info" className="mt-3 mb-0">
                    <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                    <strong>{language === 'es' ? 'Estadísticas:' : 'Statistics:'}</strong>
                    <Row className="mt-2 g-2">
                        <Col xs={6} md={4}>
                            <div className="text-center">
                                <Badge bg="primary" className="fs-6">
                                    {firstResponseStats.responsesToday || 0}
                                </Badge>
                                <br />
                                <small className="text-body-tertiary">

                                    {language === 'es' ? 'Respuestas hoy' : 'Responses today'}
                                </small>
                            </div>
                        </Col>
                        <Col xs={6} md={4}>
                            <div className="text-center">
                                <Badge bg="info" className="fs-6">
                                    {firstResponseStats.totalTrackedUsers || 0}
                                </Badge>
                                <br />
                                <small className="text-body-tertiary">

                                    {language === 'es' ? 'Usuarios' : 'Users'}
                                </small>
                            </div>
                        </Col>
                        <Col xs={12} md={4}>
                            <div className="text-center">
                                <Badge bg="success" className="fs-6">
                                    {firstResponseStats.availableContexts || 0}
                                </Badge>
                                <br />
                                <small className="text-body-tertiary">

                                    {language === 'es' ? 'Contextos' : 'Contexts'}
                                </small>
                            </div>
                        </Col>
                    </Row>
                </Alert>
            )}
        </>
    )}
</div>
                                </div>
                                
                                {config.lastUpdated && (
                                    <p className="text-muted mb-3">
                                        <small>
                                            {language === 'es'
                                                ? `Última actualización: ${new Date(config.lastUpdated).toLocaleString()}`
                                                : `Last updated: ${new Date(config.lastUpdated).toLocaleString()}`}
                                        </small>
                                    </p>
                                )}
                                
                                <div className="d-flex gap-2">
                                    <Button 
                                        variant="primary" 
                                        onClick={saveConfiguration}
                                        disabled={disabled || isSaving}
                                    >
                                        {isSaving ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                {language === 'es' ? 'Guardando...' : 'Saving...'}
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={faCog} className="me-2" />
                                                {language === 'es' ? 'Guardar Configuración' : 'Save Configuration'}
                                            </>
                                        )}
                                    </Button>
                                    
                                    <Button 
                                        variant="success" 
                                        onClick={saveScheduleConfiguration}
                                        disabled={disabled || isSavingSchedule}
                                    >
                                        {isSavingSchedule ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                {language === 'es' ? 'Guardando...' : 'Saving...'}
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={faClock} className="me-2" />
                                                {language === 'es' ? 'Guardar Horarios' : 'Save Schedule'}
                                            </>
                                        )}
                                    </Button>

                                    <Button 
    variant="info" 
    onClick={saveFirstResponseConfig}
    disabled={disabled || isSavingFirstResponse}
>
    {isSavingFirstResponse ? (
        <>
            <Spinner animation="border" size="sm" className="me-2" />
            {language === 'es' ? 'Guardando...' : 'Saving...'}
        </>
    ) : (
        <>
            <FontAwesomeIcon icon={faRobot} className="me-2" />
            {language === 'es' ? 'Guardar Primera Respuesta' : 'Save First Response'}
        </>
    )}
</Button>
                                </div>
                            </>
                        )}
                    </Tab>
                    
                    {/* ================ PESTAÑA DE HISTORIAL ================ */}
                    <Tab 
                        eventKey="history" 
                        title={
                            <span>
                                <FontAwesomeIcon icon={faHistory} className="me-2" />
                                {language === 'es' ? 'Historial' : 'History'}
                                {totalFiles > 0 && (
                                    <Badge bg="info" className="ms-2">{totalFiles}</Badge>
                                )}
                            </span>
                        }
                    >
                        <div className="d-flex justify-content-between mb-3">
                            <h5>
                                {language === 'es' 
                                    ? `Archivos procesados (${totalFiles} total)` 
                                    : `Processed files (${totalFiles} total)`}
                            </h5>
                            <Button 
                                variant="outline-secondary" 
                                size="sm"
                                onClick={loadMockHistory}
                                disabled={isLoading}
                            >
                                <FontAwesomeIcon icon={faSync} className={isLoading ? "fa-spin me-2" : "me-2"} />
                                {language === 'es' ? 'Actualizar' : 'Refresh'}
                            </Button>
                        </div>
                        
                        {isLoading ? (
                            <div className="text-center py-4">
                                <Spinner animation="border" />
                                <p className="mt-2">{language === 'es' ? 'Cargando historial...' : 'Loading history...'}</p>
                            </div>
                        ) : (
                            fileHistory.length > 0 ? (
                                <div className="table-responsive">
                                    <table className="table table-striped table-hover">
                                        <thead>
                                            <tr>
                                                <th>{language === 'es' ? 'Archivo' : 'File'}</th>
                                                <th>{language === 'es' ? 'Fecha' : 'Date'}</th>
                                                <th>{language === 'es' ? 'Contactos' : 'Contacts'}</th>
                                                <th>{language === 'es' ? 'Correcciones' : 'Corrections'}</th>
                                                <th>{language === 'es' ? 'Tiempo' : 'Time'}</th>
                                                <th>{language === 'es' ? 'Tamaño' : 'Size'}</th>
                                                <th>{language === 'es' ? 'Estado' : 'Status'}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {fileHistory.map((item, index) => (
                                                <tr key={`${item.id}-${index}`}>
                                                    <td>
                                                        <div>
                                                            <strong>{item.originalName}</strong>
                                                            <br />
                                                            <small className="text-body-tertiary">

                                                                {item.fileName || 'N/A'}
                                                            </small>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <small>{formatDate(item.processedDate)}</small>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex flex-column gap-1">
                                                            <Badge bg="success">{item.contactCount}</Badge>
                                                            {item.validContactCount !== item.contactCount && (
                                                                <small className="text-body-tertiary">

                                                                    {item.validContactCount} válidos
                                                                </small>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex flex-column gap-1">
                                                            {item.correctedPhones! > 0 && (
                                                                <Badge bg="warning">
                                                                    <FontAwesomeIcon icon={faPhone} className="me-1" />
                                                                    {item.correctedPhones}
                                                                </Badge>
                                                            )}
                                                            {item.duplicatesRemoved! > 0 && (
                                                                <Badge bg="info">
                                                                    <FontAwesomeIcon icon={faCopy} className="me-1" />
                                                                    {item.duplicatesRemoved}
                                                                </Badge>
                                                            )}
                                                            {(!item.correctedPhones || item.correctedPhones === 0) && 
                                                             (!item.duplicatesRemoved || item.duplicatesRemoved === 0) && (
                                                                <small className="text-muted">Sin correcciones</small>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <Badge bg="info" className="fs-7">
                                                            {formatProcessingTime(item.processingTimeMs)}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <small className="text-body-tertiary">

                                                            {formatFileSize(item.fileSize)}
                                                        </small>
                                                    </td>
                                                    <td>
                                                        {item.status === 'success' ? (
                                                            <Badge bg="success">
                                                                <FontAwesomeIcon icon={faCheck} className="me-1" />
                                                                {language === 'es' ? 'Éxito' : 'Success'}
                                                            </Badge>
                                                        ) : item.status === 'processing' ? (
                                                            <Badge bg="warning">
                                                                <Spinner animation="border" size="sm" className="me-1" />
                                                                {language === 'es' ? 'Procesando' : 'Processing'}
                                                            </Badge>
                                                        ) : (
                                                            <Badge bg="danger">
                                                                <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                                                                {language === 'es' ? 'Error' : 'Error'}
                                                            </Badge>
                                                        )}
                                                        {item.serverPath && (
                                                            <div>
                                                                <small className="text-success">
                                                                    <FontAwesomeIcon icon={faCheck} className="me-1" />
                                                                    {language === 'es' ? 'Respaldado' : 'Backed up'}
                                                                </small>
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    
                                    <div className="mt-4 p-3 bg-body rounded">
                                        <h6>
                                            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                                            {language === 'es' ? 'Estadísticas del historial' : 'History statistics'}
                                        </h6>
                                        <Row>
                                            <Col md={2}>
                                                <div className="text-center">
                                                    <Badge bg="primary" className="fs-6">
                                                        {fileHistory.filter(f => f.status === 'success').length}
                                                    </Badge>
                                                    <br />
                                                    <small className="text-body-tertiary">

                                                        {language === 'es' ? 'Exitosos' : 'Successful'}
                                                    </small>
                                                </div>
                                            </Col>
                                            <Col md={2}>
                                                <div className="text-center">
                                                    <Badge bg="info" className="fs-6">
                                                        {fileHistory.reduce((sum, f) => sum + f.contactCount, 0)}
                                                    </Badge>
                                                    <br />
                                                    <small className="text-body-tertiary">

                                                        {language === 'es' ? 'Contactos' : 'Contacts'}
                                                    </small>
                                                </div>
                                            </Col>
                                            <Col md={2}>
                                                <div className="text-center">
                                                    <Badge bg="warning" className="fs-6">
                                                        {fileHistory.reduce((sum, f) => sum + (f.correctedPhones || 0), 0)}
                                                    </Badge>
                                                    <br />
                                                    <small className="text-body-tertiary">

                                                        {language === 'es' ? 'Tel. corregidos' : 'Phones fixed'}
                                                    </small>
                                                </div>
                                            </Col>
                                            <Col md={2}>
                                                <div className="text-center">
                                                    <Badge bg="secondary" className="fs-6">
                                                        {fileHistory.reduce((sum, f) => sum + (f.duplicatesRemoved || 0), 0)}
                                                    </Badge>
                                                    <br />
                                                    <small className="text-body-tertiary">

                                                        {language === 'es' ? 'Duplicados' : 'Duplicates'}
                                                    </small>
                                                </div>
                                            </Col>
                                            <Col md={2}>
                                                <div className="text-center">
                                                    <Badge bg="info" className="fs-6">
                                                        {formatFileSize(fileHistory.reduce((sum, f) => sum + f.fileSize, 0))}
                                                    </Badge>
                                                    <br />
                                                    <small className="text-body-tertiary">

                                                        {language === 'es' ? 'Datos procesados' : 'Data processed'}
                                                    </small>
                                                </div>
                                            </Col>
                                            <Col md={2}>
                                                <div className="text-center">
                                                    <Badge bg="danger" className="fs-6">
                                                        {fileHistory.filter(f => f.status === 'error').length}
                                                    </Badge>
                                                    <br />
                                                    <small className="text-body-tertiary">

                                                        {language === 'es' ? 'Errores' : 'Errors'}
                                                    </small>
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>
                                </div>
                            ) : (
                                <Alert variant="info">
                                    <FontAwesomeIcon icon={faFileUpload} className="me-2" />
                                    {language === 'es' 
                                        ? 'No hay archivos procesados en el historial' 
                                        : 'No processed files in history'}
                                </Alert>
                            )
                        )}
                    </Tab>
                </Tabs>
            </Card.Body>
        </Card>
        
        {/* ================ EASTER EGG TOASTY ================ */}
        {showToasty && (
            <div
                style={{
                    position: 'fixed',
                    bottom: '20px',
                    right: showToasty ? '20px' : '-400px',
                    zIndex: 9999,
                    transition: 'right 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    background: 'linear-gradient(45deg, #000, #333)',
                    border: '4px solid #ff6600',
                    borderRadius: '15px',
                    padding: '10px 20px',
                    boxShadow: '0 0 30px rgba(255, 102, 0, 0.9), inset 0 0 15px rgba(255, 255, 255, 0.2)',
                    transform: showToasty ? 'scale(1)' : 'scale(0.3)',
                    animation: showToasty ? 'toastySlideIn 0.4s ease-out' : 'none'
                }}
            >
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '15px'
                }}>
                    <div style={{
                        width: '120px',
                        height: '120px',
                        backgroundImage: 'url(https://images.steamusercontent.com/ugc/552021066306111211/3F732B0CC4D215E74FA030FFD0C6FF9297319F81/?imw=5000&imh=5000&ima=fit&impolicy=Letterbox&imcolor=%23000000&letterbox=false)',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        border: '4px solid #000',
                        borderRadius: '12px',
                        position: 'relative',
                        boxShadow: `
                            inset 0 2px 4px rgba(255, 255, 255, 0.3), 
                            0 0 20px rgba(255, 102, 0, 0.8),
                            0 0 40px rgba(255, 51, 0, 0.6),
                            0 4px 8px rgba(0, 0, 0, 0.5)
                        `,
                        animation: 'danFordenGlow 0.8s ease-in-out infinite alternate',
                        overflow: 'hidden',
                        filter: 'contrast(1.2) saturate(1.1)'
                    }}>
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'linear-gradient(45deg, transparent 0%, rgba(255, 102, 0, 0.1) 50%, transparent 100%)',
                            animation: 'imageOverlay 1.2s ease-in-out infinite alternate'
                        }}></div>
                    </div>
                    
                    <div style={{
                        color: '#ff6600',
                        fontSize: '32px',
                        fontWeight: '900',
                        textShadow: `
                            4px 4px 0px #000,
                            -1px -1px 0px #000,
                            1px -1px 0px #000,
                            -1px 1px 0px #000,
                            1px 1px 0px #000,
                            0 0 20px #ff6600,
                            0 0 40px #ff3300
                        `,
                        fontFamily: '"Arial Black", "Helvetica", sans-serif',
                        letterSpacing: '4px',
                        transform: 'rotate(-8deg) skew(-5deg)',
                        WebkitTextStroke: '2px #000',
                        animation: 'toastyTextGlow 0.6s ease-in-out infinite alternate'
                    }}>
                        TOASTY!
                    </div>
                </div>
                
                <div style={{
                    position: 'absolute',
                    bottom: '-10px',
                    left: '10%',
                    right: '10%',
                    height: '20px',
                    background: 'linear-gradient(45deg, #ff6600, #ff3300, #ff9900)',
                    borderRadius: '50%',
                    opacity: 0.7,
                    animation: 'flames 0.3s ease-in-out infinite alternate'
                }}></div>
            </div>
        )}
        
        {/* Estilos CSS para las animaciones */}
        <style>{`
 .toast-show {
    animation: toastSlideDown 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.toast-hide {
    animation: toastSlideUp 0.3s cubic-bezier(0.55, 0.06, 0.68, 0.19);
}

@keyframes toastSlideDown {
    0% {
        opacity: 0;
        transform: translateX(-50%) translateY(-100px) scale(0.9);
    }
    100% {
        opacity: 1;
        transform: translateX(-50%) translateY(0) scale(1);
    }
}

@keyframes toastSlideUp {
    0% {
        opacity: 1;
        transform: translateX(-50%) translateY(0) scale(1);
    }
    100% {
        opacity: 0;
        transform: translateX(-50%) translateY(-100px) scale(0.9);
    }
}

.toast-alert {
    backdrop-filter: blur(10px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1) !important;
}

/* Responsive para móviles */
@media (max-width: 576px) {
    .toast-alert {
        min-width: 280px !important;
        max-width: calc(100vw - 2rem) !important;
        margin: 0 1rem;
    }
}
    
            .modal-mobile-fix .modal-dialog {
                margin: 10px;
                max-width: calc(100vw - 20px);
            }

            .modal-mobile-fix .modal-content {
                max-height: 95vh;
                display: flex;
                flex-direction: column;
            }

            .modal-mobile-fix {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    padding: 0 !important;
}

.modal-mobile-fix .modal-dialog {
    margin: 0 !important;
    max-width: 90vw !important;
    width: auto !important;
    position: relative !important;
    display: flex !important;
    align-items: center !important;
    min-height: calc(100vh - 2rem) !important;
}

.modal-mobile-fix .modal-content {
    max-height: 85vh;
    overflow-y: auto;
    width: 100%;
    margin: 1rem;
}

@media (max-width: 576px) {
    .modal-mobile-fix .modal-dialog {
        max-width: 95vw !important;
    }
    
    .modal-mobile-fix .modal-content {
        margin: 0.5rem;
        max-height: 90vh;
    }
}
            .modal-mobile-fix .modal-body {
                flex: 1;
                overflow-y: auto;
                -webkit-overflow-scrolling: touch;
            }

            .modal-mobile-fix .modal-footer {
                flex-shrink: 0;
                border-top: 1px solid #dee2e6;
            }

            @media (max-width: 576px) {
                .modal-mobile-fix .modal-dialog {
                    margin: 5px;
                    max-width: calc(100vw - 10px);
                }
                
                .modal-mobile-fix .modal-header .modal-title {
                    font-size: 0.9rem;
                    line-height: 1.2;
                }
                
                .modal-mobile-fix .table th,
                .modal-mobile-fix .table td {
                    padding: 0.25rem;
                    font-size: 0.75rem;
                }
                
                .modal-mobile-fix .btn {
                    font-size: 0.8rem;
                    padding: 0.375rem 0.5rem;
                }
                
                .modal-mobile-fix .alert {
                    padding: 0.5rem;
                    font-size: 0.8rem;
                }
                
                .modal-mobile-fix .badge {
                    font-size: 0.65rem;
                }
            }

            .sticky-top {
                position: sticky;
                top: 0;
                z-index: 1020;
            }

            .text-truncate {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            .w-md-auto {
                @media (min-width: 768px) {
                    width: auto !important;
                }
            }

            .order-md-first {
                @media (min-width: 768px) {
                    order: -1 !important;
                }
            }

            @keyframes toastySlideIn {
                0% {
                    right: -400px;
                    transform: scale(0.3) rotate(-15deg);
                    opacity: 0;
                }
                50% {
                    right: 10px;
                    transform: scale(1.2) rotate(5deg);
                    opacity: 1;
                }
                100% {
                    right: 20px;
                    transform: scale(1) rotate(0deg);
                    opacity: 1;
                }
            }
            
            @keyframes danFordenGlow {
                0% {
                    boxShadow: 
                        inset 0 2px 4px rgba(255, 255, 255, 0.3), 
                        0 0 20px rgba(255, 102, 0, 0.8),
                        0 0 40px rgba(255, 51, 0, 0.6),
                        0 4px 8px rgba(0, 0, 0, 0.5);
                    filter: contrast(1.2) saturate(1.1) brightness(1);
                }
                100% {
                    boxShadow: 
                        inset 0 4px 8px rgba(255, 255, 255, 0.5), 
                        0 0 30px rgba(255, 102, 0, 1),
                        0 0 60px rgba(255, 51, 0, 0.8),
                        0 6px 12px rgba(0, 0, 0, 0.7);
                    filter: contrast(1.3) saturate(1.2) brightness(1.1);
                }
            }
            
            @keyframes imageOverlay {
                0% {
                    background: 'linear-gradient(45deg, transparent 0%, rgba(255, 102, 0, 0.1) 50%, transparent 100%)';
                    opacity: 0.5;
                }
                100% {
                    background: 'linear-gradient(45deg, transparent 0%, rgba(255, 102, 0, 0.2) 50%, transparent 100%)';
                    opacity: 0.8;
                }
            }
            
            @keyframes toastyTextGlow {
                0% {
                    textShadow: 
                        4px 4px 0px #000,
                        -1px -1px 0px #000,
                        1px -1px 0px #000,
                        -1px 1px 0px #000,
                        1px 1px 0px #000,
                        0 0 20px #ff6600,
                        0 0 40px #ff3300;
                    transform: rotate(-8deg) skew(-5deg) scale(1);
                }
                100% {
                    textShadow: 
                        4px 4px 0px #000,
                        -1px -1px 0px #000,
                        1px -1px 0px #000,
                        -1px 1px 0px #000,
                        1px 1px 0px #000,
                        0 0 30px #ff6600,
                        0 0 60px #ff3300,
                        0 0 80px #ffff00;
                    transform: rotate(-8deg) skew(-5deg) scale(1.05);
                }
            }
            
            @keyframes flames {
                0% {
                    transform: scaleY(1) scaleX(1);
                    opacity: 0.7;
                }
                100% {
                    transform: scaleY(1.3) scaleX(1.1);
                    opacity: 0.9;
                }
            }

/* Mejoras específicas para Risk Meter Modal */
.risk-meter-modal .modal-dialog {
    max-width: 95vw !important;
}

.risk-meter-modal .modal-content {
    max-height: 90vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.risk-meter-modal .modal-body {
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding: 1rem 0.5rem;
}

.risk-meter-modal .modal-header {
    flex-shrink: 0;
    padding: 0.75rem 1rem;
}

.risk-meter-modal .modal-footer {
    flex-shrink: 0;
    padding: 0.75rem 1rem;
}

/* Responsive para móviles - Risk Meter específico */
@media (max-width: 576px) {
    .fs-7 {
        font-size: 0.75rem !important;
    }
    
    .risk-meter-modal .modal-body {
        padding: 0.5rem;
    }
    
    .risk-meter-modal .modal-header,
    .risk-meter-modal .modal-footer {
        padding: 0.5rem;
    }
    
    .risk-meter-modal .modal-header .modal-title {
        font-size: 0.9rem !important;
        line-height: 1.2;
    }
    
    .risk-meter-modal .card-body {
        padding: 0.5rem !important;
    }
    
    .risk-meter-modal .card-title {
        font-size: 0.75rem !important;
    }
    
    .risk-meter-modal .progress {
        height: 3px !important;
    }
    
    .risk-meter-modal .badge {
        font-size: 0.6rem !important;
    }
}
        `}</style>
    </>
);
};

export default FileUploadProcessor;